import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "components/Layout";
import OptionPriceDisplay from "components/OptionPriceDisplay";
import OfferDetailsModal from "components/OfferDetailsModal";
import Calendar, { toISODate } from "components/Calendar";
import api from "utils/api";
import { setLogout, setUser } from "state";
import {
  DR_HOFFMANN_SERVICES,
  formatPrice,
  individualServicePriceForPaymentMethod,
  priceForPaymentMethod,
  wakeupUnitPriceForPaymentMethod,
} from "constants/massages";
import {
  isHoffmannVoucherActive,
  isStudioLikeBooking,
} from "utils/hoffmannVoucher";
import { voucherMatchesSelection } from "constants/shopVouchers";
import { buildBuchenAuthHref } from "utils/registration";
import { isReferralBalanceEligibleOption } from "utils/referral";
import { MONTHLY_ACTION_VARIANTS, useMonthlyAction } from "utils/monthlyAction";
import "styles/booking.css";

const BERLIN_TZ = "Europe/Berlin";
const VAT_RATE = 0.19;
export const PRICE_PREVIEW_BRAND = "dr-hoffmann";
export const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: BERLIN_TZ,
  });
export const formatLong = (date) =>
  date.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: BERLIN_TZ,
  });
const OPTIONS = [
  {
    id: "wakeup",
    detailsId: "wakeup",
    deliveryGroup: "wakeup",
    serviceKey: "dr-hoffmann-wakeup-studio",
    availabilityServiceKey: "dr-hoffmann-studio",
    offerType: "wakeup_studio",
    tag: "Option 1",
    title: "Wake-Up",
    subtitle: "Schnelle Energie für deinen Tag",
    description:
      "Unsere Wake-up-Massage ist die schnelle 4-in-1-Aktivierung für deinen Körper. Sie fördert die Durchblutung, löst Verspannungen, unterstützt die Regeneration und verbessert die Beweglichkeit. Ideal, wenn du schnell neue Energie brauchst.",
    price: "5 €",
    priceNote: "/ Person",
    displayTotalLabel: "5 € / Person",
    cta: "Termin auswählen",
    requiresAuth: true,
    requiresAddress: false,
    bookingMode: "studio-weekday",
    studioLocation: "Cremon 11, 20457 Hamburg",
    disableOnlineDiscount: false,
    personCountDefault: 1,
    personCountMin: 1,
    personCountMax: 10,
    durationMinutes: 5,
    minimumLeadMinutes: 30,
  },
  {
    id: "classic",
    detailsId: "classic",
    deliveryGroup: "classic",
    serviceKey: "dr-hoffmann-classic-studio",
    availabilityServiceKey: "dr-hoffmann-studio",
    offerType: "classic_studio",
    tag: "Option 2",
    title: "Classic",
    subtitle: "Ganzkörper-Energie für deine Woche",
    description:
      "Unsere Classic-Massage ist die gezielte Ganzkörpermassage für nachhaltige Erholung und neue Energie. Mit der perkussiven Massage werden Verspannungen und Verklebungen im gesamten Körper intensiv bearbeitet, die Durchblutung gefördert und die Regeneration unterstützt. Nach einer kurzen Erholungsphase fühlt sich der Körper wieder freier, beweglicher und voller Energie an – ideal für einen ausgeglichenen Energiefluss durch die ganze Woche.",
    price: "29 €",
    priceNote: "/ 10 Min.",
    displayTotalLabel: "29 € / 10 Min.",
    spotlight: "Meist gebucht",
    cta: "Termin auswählen",
    requiresAuth: true,
    requiresAddress: false,
    bookingMode: "studio-weekday",
    studioLocation: "Cremon 11, 20457 Hamburg",
    disableOnlineDiscount: false,
    durationMinutes: 10,
    priceCents: 2900,
    minimumLeadMinutes: 30,
  },
  {
    id: "premium",
    detailsId: "intense",
    deliveryGroup: "premium",
    serviceKey: "dr-hoffmann-premium-studio",
    availabilityServiceKey: "dr-hoffmann-studio",
    offerType: "premium_studio",
    tag: "Option 3",
    title: "Intense",
    subtitle: "Neue Energie für dein Leben",
    description:
      "1 Stunde. 1 Trainer. 100 % individuell. Intense ist deine persönliche Dr.-Hoffmann-Begleitung – im Hotel, im Studio oder draußen. Ein erfahrener Trainer geht individuell auf dich ein: mit Bewegung, Stretching, Entspannung, Perkussionsmassage oder Beratung zu Training, Essen, Trinken und Regeneration. Genau das, was dein Körper gerade braucht.",
    price: "ab 70 €",
    priceNote: "/ 30 Min.",
    displayTotalLabel: "ab 70 € / 30 Min.",
    cta: "Termin auswählen",
    requiresAuth: true,
    requiresAddress: false,
    bookingMode: "studio-weekday",
    studioLocation: "Cremon 11, 20457 Hamburg",
    disableOnlineDiscount: false,
    durationMinutes: 60,
    durationDefault: 60,
    durationOptions: [30, 60],
    priceCents: 7000,
    minimumLeadMinutes: 30,
  },
];

export const WAKEUP_STUDIO_OPTION = OPTIONS.find((option) => option.id === "wakeup");
export const CLASSIC_STUDIO_OPTION = OPTIONS.find((option) => option.id === "classic");
export const PREMIUM_STUDIO_OPTION = OPTIONS.find((option) => option.id === "premium");
export const WAKEUP_ONSITE_OPTION = {
  ...WAKEUP_STUDIO_OPTION,
  serviceKey: "dr-hoffmann-wakeup-onsite",
  legacyServiceKey: "dr-hoffmann-wakeup-onsite-voucher",
  availabilityServiceKey: "dr-hoffmann-mobile",
  offerType: "wakeup_onsite",
  description:
    "Unsere Wake-up-Massage ist die schnelle 4-in-1-Aktivierung für deinen Körper. Sie fördert die Durchblutung, löst Verspannungen, unterstützt die Regeneration und verbessert die Beweglichkeit. Ideal, wenn du schnell neue Energie brauchst.",
  requiresAddress: true,
  bookingMode: "mobile",
  studioLocation: undefined,
  badge: "Anfahrt 39 € - mit Gutschein 0 €",
  hint: "Gutschein nur einmalig einlösbar und nicht kombinierbar.",
  skipPostalCheck: true,
  requiresVoucherQuestion: true,
};
export const CLASSIC_ONSITE_OPTION = {
  ...CLASSIC_STUDIO_OPTION,
  serviceKey: "dr-hoffmann-classic-onsite",
  legacyServiceKey: "dr-hoffmann-mobile",
  availabilityServiceKey: "dr-hoffmann-mobile",
  offerType: "classic_onsite",
  description:
    "Unsere Classic-Massage ist die gezielte Ganzkörpermassage für nachhaltige Erholung und neue Energie. Mit der perkussiven Massage werden Verspannungen und Verklebungen im gesamten Körper intensiv bearbeitet, die Durchblutung gefördert und die Regeneration unterstützt. Nach einer kurzen Erholungsphase fühlt sich der Körper wieder freier, beweglicher und voller Energie an – ideal für einen ausgeglichenen Energiefluss durch die ganze Woche.",
  requiresAddress: true,
  bookingMode: "mobile",
  studioLocation: undefined,
  badge: "Anfahrt 39 € - mit Gutschein 0 €",
  skipPostalCheck: true,
  requiresVoucherQuestion: true,
};
export const PREMIUM_ONSITE_OPTION = {
  ...PREMIUM_STUDIO_OPTION,
  serviceKey: "dr-hoffmann-premium-onsite",
  availabilityServiceKey: "dr-hoffmann-mobile",
  offerType: "premium_onsite",
  description:
    "1 Stunde. 1 Trainer. 100 % individuell. Intense ist deine persönliche Dr.-Hoffmann-Begleitung – im Hotel, im Studio oder draußen. Ein erfahrener Trainer geht individuell auf dich ein: mit Bewegung, Stretching, Entspannung, Perkussionsmassage oder Beratung zu Training, Essen, Trinken und Regeneration. Genau das, was dein Körper gerade braucht.",
  requiresAddress: true,
  bookingMode: "mobile",
  studioLocation: undefined,
  badge: "Anfahrt 39 € - mit Gutschein 0 €",
  skipPostalCheck: true,
  requiresVoucherQuestion: true,
};

const HOFFMANN_BUSINESS_EVENT_BOOKING_CUTOFF = new Date("2026-06-14T16:00:00+02:00");
const HOFFMANN_BUSINESS_EVENT_START_ISO = "2026-06-14T16:00:00+02:00";
const HOFFMANN_BUSINESS_EVENT_SCHEDULE_LABEL =
  "14.06.2026 - Ab 16 Uhr - 19 Uhr Anpfiff";
const HOFFMANN_BUSINESS_EVENT_LOCATION = "Cremon 11, 20457 Hamburg";
const HOFFMANN_BUSINESS_EVENT_MAX_SEATS_PER_USER = 10;

export const WAKEUP_PLUS_BUSINESS_OPTION = {
  id: "wakeup-plus-business",
  detailsId: "wakeup-plus-business",
  serviceKey: "dr-hoffmann-wakeup-plus-business",
  availabilityServiceKey: "dr-hoffmann-mobile",
  offerType: "wakeup_plus_business",
  tag: "Option 1",
  title: "Wake-Up+",
  subtitle: "Für Neukunden & Veranstaltungen",
  description:
    "Für Unternehmen, die ihre Mitarbeiter langanhaltend fit und produktiv halten möchten. Wake-Up Massage für Teams ab 1 Person, maximal bis zu 30 Min.",
  price: "ab 79 €",
  priceNote: "netto zzgl. MwSt.",
  displayTotalLabel: "ab 79 € netto zzgl. MwSt.",
  spotlight: "Meist gebucht",
  cta: "Termin auswählen",
  requiresAuth: true,
  requiresAddress: true,
  bookingMode: "mobile",
  disableOnlineDiscount: true,
  skipPostalCheck: true,
  personCountDefault: 10,
  personCountMin: 1,
  durationMinutes: 30,
};

export const CLASSIC_PLUS_STUDIO_OPTION = {
  id: "classic-plus-business",
  detailsId: "classic-plus-business",
  deliveryGroup: "classic-plus-business",
  serviceKey: "dr-hoffmann-classic-plus-business-studio",
  availabilityServiceKey: "dr-hoffmann-classic-plus-business-studio",
  offerType: "classic_plus_business_studio",
  tag: "Option 2",
  title: "Classic+",
  subtitle: "Löst Ganzkörperverspannungen",
  description:
    "10 Minuten Classic Massage für Unternehmer im Studio – gezielte Lockerung und neue Energie.",
  price: "29 €",
  priceNote: "netto zzgl. MwSt.",
  displayTotalLabel: "29 € netto zzgl. MwSt.",
  cta: "Termin auswählen",
  requiresAuth: true,
  requiresAddress: false,
  bookingMode: "studio-weekday",
  studioLocation: "Cremon 11, 20457 Hamburg",
  disableOnlineDiscount: true,
  stripeOnly: true,
  durationMinutes: 10,
  priceCents: 2900,
  minimumLeadMinutes: 30,
};

export const CLASSIC_PLUS_ONSITE_OPTION = {
  ...CLASSIC_PLUS_STUDIO_OPTION,
  serviceKey: "dr-hoffmann-classic-plus-business-onsite",
  availabilityServiceKey: "dr-hoffmann-classic-plus-business-onsite",
  offerType: "classic_plus_business_onsite",
  description:
    "10 Minuten Classic Massage für Unternehmer bei Ihnen vor Ort – gezielte Lockerung und neue Energie.",
  requiresAddress: true,
  bookingMode: "mobile",
  studioLocation: undefined,
  badge: "Anfahrt 39 € netto – mit Gutschein 0 €",
  skipPostalCheck: true,
  requiresVoucherQuestion: true,
};

export const INTENSE_PLUS_STUDIO_OPTION = {
  id: "intense-plus-business",
  detailsId: "intense-plus-business",
  deliveryGroup: "intense-plus-business",
  serviceKey: "dr-hoffmann-intense-plus-business-studio",
  availabilityServiceKey: "dr-hoffmann-intense-plus-business-studio",
  offerType: "intense_plus_business_studio",
  tag: "Option 3",
  title: "Intense+",
  subtitle: "Personal Training für dein Team",
  description:
    "60 Minuten. 1 Trainer. 100 % individuell bei uns im Studio – mit Bewegung, Stretching, Entspannung, Perkussionsmassage oder Beratung zu Training, Essen, Trinken und Regeneration.",
  price: "125 €",
  priceNote: "netto pro Stunde zzgl. MwSt.",
  displayTotalLabel: "125 € pro Stunde netto zzgl. MwSt.",
  cta: "Termin auswählen",
  requiresAuth: true,
  requiresAddress: false,
  bookingMode: "studio-weekday",
  studioLocation: "Cremon 11, 20457 Hamburg",
  disableOnlineDiscount: true,
  stripeOnly: true,
  durationOptions: Array.from({ length: 8 }, (_, index) => (index + 1) * 60),
  durationDefault: 60,
  durationMinutes: 60,
  priceCents: 12500,
  minimumLeadMinutes: 30,
};

export const INTENSE_PLUS_ONSITE_OPTION = {
  ...INTENSE_PLUS_STUDIO_OPTION,
  serviceKey: "dr-hoffmann-intense-plus-business",
  availabilityServiceKey: "dr-hoffmann-intense-plus-business",
  offerType: "intense_plus_business_onsite",
  description:
    "60 Minuten. 1 Trainer. 100 % individuell bei Ihnen vor Ort – mit Bewegung, Stretching, Entspannung, Perkussionsmassage oder Beratung zu Training, Essen, Trinken und Regeneration.",
  requiresAddress: true,
  bookingMode: "mobile",
  studioLocation: undefined,
  badge: "Anfahrt 39 € netto – mit Gutschein 0 €",
  skipPostalCheck: true,
  requiresVoucherQuestion: true,
};

export const BUSINESS_OPTIONS = [
  WAKEUP_PLUS_BUSINESS_OPTION,
  CLASSIC_PLUS_STUDIO_OPTION,
  INTENSE_PLUS_STUDIO_OPTION,
];

const SPECIAL_REQUEST_OPTION = {
  id: "special-request",
  tag: "Option 6",
  title: "Sonderwünsche?",
  description:
    "Für größere Gruppen, Events, Massageadressen außerhalb Hamburgs oder individuelle Wünsche.",
  price: "Auf Anfrage",
  cta: "Sonderanfrage stellen",
  requiresAuth: false,
  specialRequestPath: "/kontakt?reason=sonderwunsch",
};

export const LEGACY_OPTIONS = [
  {
    id: "starterpaket",
    serviceKey: "dr-hoffmann-consultation",
    tag: "Option 4",
    title: "Starterpaket buchen",
    description:
      "Inklusive attraktivem Neukundenpaket: 6 kostenfreie Massagen vor Ort, ein Social-Media-Blumenstrauß sowie ein professioneller Werbefilm für das Unternehmen nach Wahl - ideal zur Neukundengewinnung.",
    price: "499 €",
    priceCents: Math.round(49900 * (1 + VAT_RATE)),
    priceNetCents: 49900,
    priceNote: "netto zzgl. MwSt.",
    requiresAuth: true,
    requiresAddress: false,
    bookingMode: "consultation",
    paidPackage: true,
    stripeOnly: true,
    hoffmannBookingOption: "Starterpaket buchen",
    durationMinutes: 30,
  },
  {
    id: "lizenzpartner",
    serviceKey: "dr-hoffmann-consultation",
    deliveryGroup: "license-partner",
    tag: "Option 5",
    title: "Lizenzpartner werden",
    highlight: "Kostenloser Erstbesuch inkl.",
    description:
      "bis zu 6 Test-Massagen und Location Check.",
    requiresAuth: true,
    requiresAddress: false,
    bookingMode: "consultation",
    hoffmannBookingOption: "Lizenzpartner werden",
    durationMinutes: 30,
  },
];

const LICENSE_PARTNER_STUDIO_OPTION = {
  ...LEGACY_OPTIONS.find((option) => option.id === "lizenzpartner"),
  deliveryGroup: "license-partner",
  licensePartnerLocation: "studio",
  studioLocation: "Cremon 11, 20457 Hamburg",
};

const LICENSE_PARTNER_ONSITE_OPTION = {
  ...LICENSE_PARTNER_STUDIO_OPTION,
  bookingMode: "mobile",
  offerType: "license_partner_onsite",
  licensePartnerLocation: "onsite",
  requiresAddress: true,
  requiresLicensePartnerLocation: true,
  skipPostalCheck: true,
  badge: "39 € Anfahrtspauschale",
};

const LEGACY_OPTION_IDS = new Set([
  ...LEGACY_OPTIONS.map((option) => option.id),
  "license-partner",
]);
const ALL_OPTIONS = [...OPTIONS, ...BUSINESS_OPTIONS];
const AUDIENCE_INDIVIDUAL = "individual";
const AUDIENCE_BUSINESS = "business";
const normalizeAudienceId = (id) =>
  id === AUDIENCE_BUSINESS ? AUDIENCE_BUSINESS : AUDIENCE_INDIVIDUAL;
const BOOKING_AUDIENCES = [
  {
    id: AUDIENCE_INDIVIDUAL,
    label: "Privat",
    title: "Für Privatpersonen und flexible Termine",
  },
  {
    id: AUDIENCE_BUSINESS,
    label: "Geschäftlich",
    title: "Für Unternehmen und Partner",
  },
];
const BUSINESS_ONLY_OPTION_IDS = new Set(
  BUSINESS_OPTIONS
    .map((option) => option.id)
);
const audienceForOption = (option) =>
  BUSINESS_ONLY_OPTION_IDS.has(option?.id) ? AUDIENCE_BUSINESS : AUDIENCE_INDIVIDUAL;
const DELIVERY_STUDIO_OPTIONS = {
  wakeup: WAKEUP_STUDIO_OPTION,
  classic: CLASSIC_STUDIO_OPTION,
  premium: PREMIUM_STUDIO_OPTION,
  "classic-plus-business": CLASSIC_PLUS_STUDIO_OPTION,
  "intense-plus-business": INTENSE_PLUS_STUDIO_OPTION,
  "license-partner": LICENSE_PARTNER_STUDIO_OPTION,
};
const DELIVERY_ONSITE_OPTIONS = {
  wakeup: WAKEUP_ONSITE_OPTION,
  classic: CLASSIC_ONSITE_OPTION,
  premium: PREMIUM_ONSITE_OPTION,
  "classic-plus-business": CLASSIC_PLUS_ONSITE_OPTION,
  "intense-plus-business": INTENSE_PLUS_ONSITE_OPTION,
  "license-partner": LICENSE_PARTNER_ONSITE_OPTION,
};
const deliveryGroupForOption = (option) => option?.deliveryGroup || option?.id;
export const isDeliveryChoiceOption = (option) =>
  Boolean(DELIVERY_STUDIO_OPTIONS[deliveryGroupForOption(option)]) ||
  option?.id === "lizenzpartner";
export const optionForDelivery = (optionOrGroup, comesToYou) => {
  const group =
    typeof optionOrGroup === "string"
      ? optionOrGroup
      : deliveryGroupForOption(optionOrGroup);
  return comesToYou
    ? DELIVERY_ONSITE_OPTIONS[group]
    : DELIVERY_STUDIO_OPTIONS[group];
};
const wakeupComesToYouFromId = (id) =>
  id === "wakeup-onsite" ||
  id === "wakeup-onsite-voucher" ||
  id === "classic-onsite" ||
  id === "premium-onsite" ||
  id === "classic-plus-business-onsite" ||
  id === "intense-plus-business-onsite";

export const defaultPaymentForOption = (option) =>
  isBusinessEventOption(option)
    ? "on-site"
    : requiresStripeOnlyPayment(option)
    ? "stripe"
    : option?.bookingMode === "consultation" && !option?.paidPackage
    ? "later"
    : "stripe";

export const defaultPersonCountForOption = (option) =>
  Number(option?.personCountDefault || 1);

export const defaultDurationForOption = (option) =>
  Number(option?.durationDefault ?? option?.durationMinutes ?? 60);

export const initialDurationMinutesForOption = (option) =>
  durationChoicesForOption(option).length > 0
    ? ""
    : defaultDurationForOption(option);

export const durationChoicesForOption = (option) => {
  if (!option?.durationOptions?.length) return [];
  return option.durationOptions.map((minutes) => Number(minutes));
};

export const isPremiumBookingOption = (option) =>
  option?.offerType === "premium_studio" || option?.offerType === "premium_onsite";

export const isWakeupPlusOption = (option) =>
  option?.offerType === "wakeup_plus_business";

export const isIntensePlusBusinessOption = (option) =>
  String(option?.offerType || "").startsWith("intense_plus_business");

export const isBusinessPlusOption = (option) =>
  String(option?.offerType || "").startsWith("classic_plus_business") ||
  isIntensePlusBusinessOption(option);

export const isWakeupMobileOption = (option) =>
  option?.offerType === "wakeup_onsite" || option?.offerType === "wakeup_plus_business";

export const isWakeupBookingOption = (option) =>
  String(option?.offerType || "").startsWith("wakeup_");

export const isDeliveryOnsiteOption = (option) =>
  [
    "wakeup_onsite",
    "classic_onsite",
    "premium_onsite",
    "classic_plus_business_onsite",
    "intense_plus_business_onsite",
  ].includes(option?.offerType) || option?.offerType === "license_partner_onsite";

export const hasAdjustableTravelFee = (option) =>
  isDeliveryOnsiteOption(option) && option?.offerType !== "wakeup_plus_business";

export const isFixedGrossIndividualOption = (option) =>
  [
    "wakeup_studio",
    "wakeup_onsite",
    "classic_studio",
    "classic_onsite",
    "premium_studio",
    "premium_onsite",
    "license_partner_onsite",
  ].includes(option?.offerType);

export const usesIndividualSchedule = (option) =>
  (isFixedGrossIndividualOption(option) ||
    ["classic_plus_business_studio", "intense_plus_business_studio"].includes(
      option?.offerType
    )) &&
  !isDeliveryOnsiteOption(option);

export const usesDailyHoffmannSchedule = (option) =>
  usesIndividualSchedule(option) || isDeliveryOnsiteOption(option);

export const availabilityServiceKeyForOption = (option) => {
  if (option?.offerType === "wakeup_studio") return "dr-hoffmann-wakeup-studio";
  if (option?.offerType === "wakeup_onsite") return "dr-hoffmann-wakeup-onsite";
  if (option?.offerType === "classic_studio") return "dr-hoffmann-classic-studio";
  if (option?.offerType === "classic_onsite") return "dr-hoffmann-classic-onsite";
  if (option?.offerType === "premium_studio") return "dr-hoffmann-premium-studio";
  if (option?.offerType === "premium_onsite") return "dr-hoffmann-premium-onsite";
  if (String(option?.offerType || "").startsWith("classic_plus_business")) {
    return option.serviceKey;
  }
  if (String(option?.offerType || "").startsWith("intense_plus_business")) {
    return option.serviceKey;
  }
  if (option?.offerType === "wakeup_plus_business") {
    return "dr-hoffmann-wakeup-plus-business";
  }
  if (usesIndividualSchedule(option)) return "dr-hoffmann-studio";
  return option?.availabilityServiceKey || option?.serviceKey;
};

export const WAKEUP_MINUTES_PER_PERSON = 5;

export const wakeupServiceDurationMinutes = (option, count) =>
  isWakeupPlusOption(option)
    ? Number(option?.durationMinutes || 10)
    : isWakeupMobileOption(option)
    ? Math.max(1, Number(count || option?.personCountDefault || 1) * WAKEUP_MINUTES_PER_PERSON)
    : WAKEUP_MINUTES_PER_PERSON;

const fullAddressFromServiceAddress = (addr) => {
  if (!addr || typeof addr !== "object") return "";
  const explicit = String(addr?.fullAddress || addr?.address || "").trim();
  if (explicit) return explicit;
  const street = String(addr?.street || "").trim();
  const postalCode = String(addr?.postalCode || "").trim();
  const city = String(addr?.city || "").trim();
  return [street, postalCode, city].filter(Boolean).join(" ").trim();
};

const hasAddressDetails = (addr) =>
  Boolean(
    fullAddressFromServiceAddress(addr) ||
      String(addr?.street || "").trim() ||
      String(addr?.postalCode || "").trim() ||
      String(addr?.city || "").trim()
  );

const customerDisplayName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();

const addressLooksLikeCustomerName = (addr, user) => {
  const names = [user?.firstName, user?.lastName, customerDisplayName(user)]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  if (!names.length) return false;
  const values = [
    fullAddressFromServiceAddress(addr),
    addr?.street,
    addr?.fullAddress,
    addr?.notes,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  return values.length > 0 && values.every((value) => names.includes(value));
};

const addressPrefillFromUser = (user) => {
  const saved = user?.customerAddress;
  const stored = hasAddressDetails(saved) ? saved : user?.lastServiceAddress || {};
  const fullAddress = fullAddressFromServiceAddress(stored);
  const parsed = splitInvoiceAddressLine(fullAddress);
  const displayName = customerDisplayName(user);
  return {
    companyName: String(displayName || stored.companyName || "").trim(),
    fullAddress,
    street: String(stored.street && stored.street !== fullAddress ? stored.street : parsed.street).trim(),
    postalCode: String(stored.postalCode || parsed.postalCode || "").trim(),
    city: String(stored.city || parsed.city || "").trim(),
    notes: "",
  };
};

const extractPlzFromText = (value) => {
  const match = String(value || "").match(/\b(\d{5})\b/);
  return match ? match[1] : "";
};

const splitInvoiceAddressLine = (value) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  const match = text.match(/^(.*?)(?:,\s*|\s+)(\d{5})\s+(.+)$/);
  if (!match) return { street: text, postalCode: "", city: "" };
  return {
    street: match[1].replace(/,\s*$/, "").trim(),
    postalCode: match[2],
    city: match[3].trim(),
  };
};

const buildServiceAddress = ({ companyName, fullAddress, notes, geocodedAddress }) => {
  const postalCode =
    extractPlzFromText(geocodedAddress) || extractPlzFromText(fullAddress);
  return {
    companyName,
    fullAddress,
    address: fullAddress,
    street: fullAddress,
    postalCode,
    city: "",
    notes,
  };
};

export const buildServiceAddressForOption = (option, addr) => {
  if (option?.usesLegacyAddressFields) return addr;
  return buildServiceAddress(addr);
};

export const buildLocalPricePreview = (
  option,
  count,
  hasVoucher = false,
  applyTravelFee = true,
  paymentMethod = "on-site",
  user = null,
  durationMinutes
) => {
  if (!option?.offerType) return null;
  const personCount = Math.max(1, Number(count || option.personCountDefault || 1));
  const selectedDurationMinutes = Number(
    durationMinutes ?? option?.durationMinutes ?? 60
  );
  let serviceNetCents = 0;
  let travelNetCents;
  let travelOriginalNetCents;
  if (option.offerType === "wakeup_studio") {
    serviceNetCents =
      personCount * wakeupUnitPriceForPaymentMethod(paymentMethod, user);
  } else if (option.offerType === "wakeup_onsite") {
    serviceNetCents =
      personCount * wakeupUnitPriceForPaymentMethod(paymentMethod, user);
    travelOriginalNetCents = 3900;
    travelNetCents = hasVoucher || !applyTravelFee ? 0 : 3900;
  } else if (option.offerType === "classic_studio") {
    serviceNetCents =
      individualServicePriceForPaymentMethod(option.serviceKey, paymentMethod) ?? 2900;
  } else if (option.offerType === "classic_onsite") {
    serviceNetCents =
      individualServicePriceForPaymentMethod(option.serviceKey, paymentMethod) ?? 2900;
    travelOriginalNetCents = 3900;
    travelNetCents = hasVoucher || !applyTravelFee ? 0 : 3900;
  } else if (option.offerType === "premium_studio") {
    serviceNetCents =
      individualServicePriceForPaymentMethod(
        option.serviceKey,
        paymentMethod,
        selectedDurationMinutes
      ) ?? (selectedDurationMinutes === 30 ? 8000 : 12500);
  } else if (option.offerType === "premium_onsite") {
    serviceNetCents =
      individualServicePriceForPaymentMethod(
        option.serviceKey,
        paymentMethod,
        selectedDurationMinutes
      ) ?? (selectedDurationMinutes === 30 ? 8000 : 12500);
    travelOriginalNetCents = 3900;
    travelNetCents = hasVoucher || !applyTravelFee ? 0 : 3900;
  } else if (option.offerType === "wakeup_plus_business") {
    serviceNetCents = 7900 + Math.max(0, personCount - 10) * 500;
    travelNetCents = 0;
  } else if (option.offerType === "classic_plus_business_studio") {
    serviceNetCents = 2900;
  } else if (option.offerType === "classic_plus_business_onsite") {
    serviceNetCents = 2900;
    travelOriginalNetCents = 3900;
    travelNetCents = hasVoucher || !applyTravelFee ? 0 : 3900;
  } else if (option.offerType === "intense_plus_business_studio") {
    serviceNetCents = 12500 * (selectedDurationMinutes / 60);
  } else if (option.offerType === "intense_plus_business_onsite") {
    serviceNetCents = 12500 * (selectedDurationMinutes / 60);
    travelOriginalNetCents = 3900;
    travelNetCents = hasVoucher || !applyTravelFee ? 0 : 3900;
  } else if (option.offerType === "license_partner_onsite") {
    serviceNetCents = 0;
    travelOriginalNetCents = 3900;
    travelNetCents = applyTravelFee ? 3900 : 0;
  } else if (option.offerType === "business_event") {
    const unitGrossCents = Number(option.unitPriceCents || option.priceCents || 0);
    const grossCents = unitGrossCents * personCount;
    const netCents = Math.round(grossCents / (1 + VAT_RATE));
    return {
      netCents,
      vatCents: grossCents - netCents,
      grossCents,
      source: "local",
    };
  } else {
    return null;
  }
  const netCents = serviceNetCents + (travelNetCents || 0);
  const vatCents = isFixedGrossIndividualOption(option)
    ? 0
    : Math.round(netCents * VAT_RATE);
  return {
    serviceNetCents,
    travelNetCents,
    travelOriginalNetCents,
    netCents,
    vatCents,
    grossCents: netCents + vatCents,
    source: "local",
  };
};

export const normalizePricePreview = (data) => {
  const source = data?.pricePreview || data?.preview || data;
  const serviceNetCents = Number(
    source?.serviceNetCents ?? source?.serviceAmountNetCents
  );
  const travelNetCents = Number(
    source?.travelNetCents ?? source?.travelAmountNetCents ?? source?.arrivalNetCents
  );
  const travelOriginalNetCents = Number(
    source?.travelOriginalNetCents ?? source?.arrivalOriginalNetCents
  );
  const netCents = Number(
    source?.netCents ?? source?.netAmountCents ?? source?.netPriceCents
  );
  const vatCents = Number(source?.vatCents ?? source?.taxCents);
  const grossCents = Number(source?.grossCents ?? source?.totalCents ?? source?.priceCents);
  if (![netCents, vatCents, grossCents].every(Number.isFinite)) return null;
  return {
    serviceNetCents: Number.isFinite(serviceNetCents) ? serviceNetCents : undefined,
    travelNetCents: Number.isFinite(travelNetCents) ? travelNetCents : undefined,
    travelOriginalNetCents: Number.isFinite(travelOriginalNetCents)
      ? travelOriginalNetCents
      : undefined,
    netCents,
    vatCents,
    grossCents,
    source: "backend",
  };
};

const normalizeOptionId = (id) => {
  if (
    id === "wakeup-studio" ||
    id === "wakeup-onsite" ||
    id === "wakeup-onsite-voucher"
  ) {
    return "wakeup";
  }
  if (id === "classic-studio" || id === "classic-onsite") {
    return "classic";
  }
  if (id === "premium-studio" || id === "premium-onsite") {
    return "premium";
  }
  if (id === "classic-plus-business-onsite") {
    return "classic-plus-business";
  }
  if (id === "intense-plus-business-onsite") {
    return "intense-plus-business";
  }
  if (id === "voucher-studio" || id === "voucher-chair" || id === "sessl") {
    return "studio";
  }
  if (id === "business-event") {
    return "wm-aktion";
  }
  return id;
};

export const isThursdayOnlyStudio = (option) => option?.bookingMode === "studio";
export const isLicensePartnerOption = (option) => option?.id === "lizenzpartner";
export const isLicensePartnerOnsiteOption = (option) =>
  isLicensePartnerOption(option) && option?.licensePartnerLocation === "onsite";
export const isStarterPackageOption = (option) => option?.id === "starterpaket";
export const isBusinessEventOption = (option) =>
  option?.offerType === "business_event" ||
  option?.id === "wm-aktion" ||
  option?.id === "business-event";
const requiresStripeOnlyPayment = (option) =>
  Boolean(option?.stripeOnly) ||
  isWakeupPlusOption(option) ||
  (Boolean(option?.paidPackage) && !isBusinessEventOption(option));
const disablesOnsitePaymentChoice = (option) =>
  isFixedGrossIndividualOption(option);
const paymentMethodForOption = (option, paymentMethod) =>
  disablesOnsitePaymentChoice(option) && paymentMethod === "on-site"
    ? "stripe"
    : paymentMethod;
const isAfterBusinessEventCutoff = (date = new Date()) =>
  date.getTime() >= HOFFMANN_BUSINESS_EVENT_BOOKING_CUTOFF.getTime();
export const skipsTimeslotSelection = (option) =>
  isStarterPackageOption(option) || isBusinessEventOption(option);

export const bookingHoursHintForOption = (option, date) => {
  if (!option || isStarterPackageOption(option)) return null;
  if (isThursdayIndividualStudioBooking(option, date)) {
    return "Termine sind zwischen 10:00 und 22:00 Uhr buchbar.";
  }
  if (isThursdayOnlyStudio(option) || isLicensePartnerOption(option)) {
    return "Termine sind zwischen 12:00 und 19:30 Uhr buchbar.";
  }
  if (isBusinessEventOption(option)) {
    return null;
  }
  if (isLicensePartnerOption(option)) {
    return "Termine sind zwischen 12:00 und 19:30 Uhr buchbar.";
  }
  if (
    usesIndividualSchedule(option) ||
    isDeliveryOnsiteOption(option) ||
    isWakeupPlusOption(option)
  ) {
    if (option.offerType === "wakeup_studio") {
      return "Termine sind zwischen 10:00 und 22:00 Uhr buchbar. Im Studio w\u00e4hlbar in 5-Minuten-Abst\u00e4nden.";
    }
    return "Termine sind zwischen 10:00 und 22:00 Uhr buchbar.";
  }
  return null;
};

const berlinWeekdayIndex = (date) => {
  const shortWeekday = new Intl.DateTimeFormat("en-US", {
    timeZone: BERLIN_TZ,
    weekday: "short",
  }).format(new Date(date));
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(shortWeekday);
};

export const isThursdayIndividualStudioBooking = (option, date) =>
  Boolean(option && date && usesIndividualSchedule(option) && berlinWeekdayIndex(date) === 4);

export const filterSlotsForOption = (_option, _date, slotList) => {
  if (!Array.isArray(slotList)) return [];
  return slotList;
};

export const studioScheduleForOption = (option, date) =>
  usesIndividualSchedule(option)
    ? "weekday"
    : isThursdayIndividualStudioBooking(option, date)
    ? "thursday"
    : "weekday";

export const slotStepMinutesForOption = (option) =>
  option?.offerType === "classic_studio" || isPremiumBookingOption(option)
    ? 15
    : undefined;

const HOFFMANN_OPTION1_CUTOFF = new Date(2026, 5, 30, 23, 59, 59, 999);
export const isAfterHoffmannOption1Cutoff = (date) =>
  Boolean(date) && new Date(date).getTime() > HOFFMANN_OPTION1_CUTOFF.getTime();

export const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addBusinessDays = (date, days) => {
  const result = startOfDay(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const weekday = result.getDay();
    if (weekday !== 0 && weekday !== 6) added += 1;
  }
  return result;
};

export const licensePartnerMinDate = () => addBusinessDays(new Date(), 2);

export const arrangedAppointmentStartISO = () => {
  const placeholder = addBusinessDays(new Date(), 10);
  placeholder.setHours(12, 0, 0, 0);
  return placeholder.toISOString();
};

const isFreeConsultation = (option) =>
  option?.bookingMode === "consultation" && !option?.paidPackage;

const CREMON_STUDIO_LOCATION = "Cremon 11, 20457 Hamburg";
const DEFAULT_STUDIO_LOCATION =
  DR_HOFFMANN_SERVICES.find((s) => s.key === "dr-hoffmann-studio")?.locationDefault ||
  CREMON_STUDIO_LOCATION;

const studioLocationForDate = () => DEFAULT_STUDIO_LOCATION;

export const studioLocationForOption = (option, date) =>
  usesIndividualSchedule(option)
    ? CREMON_STUDIO_LOCATION
    : option?.studioLocation || studioLocationForDate(date);

const BOOKING_DRAFT_STORAGE_KEY = "drHoffmannBookingDraft";
const BOOKING_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const initialBookingConsents = {
  legalTerms: false,
};

const readBookingDraft = () => {
  if (typeof window === "undefined") return null;
  for (const storageName of ["localStorage", "sessionStorage"]) {
    try {
      const storage = window[storageName];
      const raw = storage.getItem(BOOKING_DRAFT_STORAGE_KEY);
      if (!raw) continue;
      const draft = JSON.parse(raw);
      if (draft.savedAt && Date.now() - draft.savedAt > BOOKING_DRAFT_MAX_AGE_MS) {
        storage.removeItem(BOOKING_DRAFT_STORAGE_KEY);
        continue;
      }
      return draft;
    } catch {
      /* unavailable storage or malformed draft */
    }
  }
  return null;
};

const saveBookingDraft = (draft) => {
  if (typeof window === "undefined" || !draft) return;
  const value = JSON.stringify({ ...draft, savedAt: Date.now() });
  try {
    window.localStorage.setItem(BOOKING_DRAFT_STORAGE_KEY, value);
    window.sessionStorage.removeItem(BOOKING_DRAFT_STORAGE_KEY);
    return;
  } catch {
    /* Fall back to the current tab if local storage is unavailable. */
  }
  try {
    window.sessionStorage.setItem(BOOKING_DRAFT_STORAGE_KEY, value);
  } catch {
    /* quota / private mode */
  }
};

const clearBookingDraft = () => {
  if (typeof window === "undefined") return;
  for (const storageName of ["localStorage", "sessionStorage"]) {
    try {
      const storage = window[storageName];
      storage.removeItem(BOOKING_DRAFT_STORAGE_KEY);
    } catch {
      /* unavailable storage */
    }
  }
};

const PersonCountCounter = ({ id, value, min = 1, max, onChange }) => {
  const holdTimerRef = useRef(null);
  const holdIntervalRef = useRef(null);
  const currentValueRef = useRef(value);
  useEffect(() => {
    currentValueRef.current = value;
  }, [value]);
  const nextValue = useCallback(
    (delta) => {
      const numeric = Number(currentValueRef.current) || min;
      const upper = Number.isFinite(Number(max)) ? Number(max) : Infinity;
      const bounded = Math.min(upper, Math.max(min, numeric + delta));
      currentValueRef.current = bounded;
      onChange(bounded);
    },
    [max, min, onChange]
  );
  const stopHold = useCallback(() => {
    window.clearTimeout(holdTimerRef.current);
    window.clearInterval(holdIntervalRef.current);
    holdTimerRef.current = null;
    holdIntervalRef.current = null;
  }, []);
  const startHold = useCallback(
    (delta) => {
      nextValue(delta);
      stopHold();
      holdTimerRef.current = window.setTimeout(() => {
        holdIntervalRef.current = window.setInterval(() => nextValue(delta), 90);
      }, 350);
    },
    [nextValue, stopHold]
  );

  useEffect(() => stopHold, [stopHold]);

  return (
    <div className="person-counter">
      <button
        type="button"
        className="person-counter-btn"
        onPointerDown={() => startHold(-1)}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
        onPointerCancel={stopHold}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") startHold(-1);
        }}
        onKeyUp={stopHold}
        disabled={Number(value) <= min}
        aria-label="Mitarbeitende verringern"
      >
        -
      </button>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step="1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="person-counter-btn"
        onPointerDown={() => startHold(1)}
        onPointerUp={stopHold}
        onPointerLeave={stopHold}
        onPointerCancel={stopHold}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") startHold(1);
        }}
        onKeyUp={stopHold}
        disabled={Number.isFinite(Number(max)) && Number(value) >= Number(max)}
        aria-label="Mitarbeitende erhöhen"
      >
        +
      </button>
    </div>
  );
};

const draftFromState = ({
  selectedOption,
  selectedDate,
  selectedSlot,
  consultationNotes,
  address,
  invoiceAddress,
  serviceAreaCheck,
  paymentMethod,
  personCount,
  durationMinutes,
  hasVoucher,
  voucherTermsAccepted,
  bookingPhone,
  wakeupComesToYou,
  bookingConsents,
  applyReferralBalance,
  selectedMassageVoucherId,
  monthlyActionVariant,
}) => ({
  optionId: selectedOption?.id || null,
  selectedDate: selectedDate ? toISODate(selectedDate) : null,
  selectedSlot,
  consultationNotes,
  address,
  invoiceAddress,
  serviceAreaCheck,
  paymentMethod,
  personCount,
  durationMinutes,
  hasVoucher,
  voucherTermsAccepted,
  bookingPhone,
  wakeupComesToYou,
  bookingConsents,
  applyReferralBalance,
  selectedMassageVoucherId,
  monthlyActionVariant,
});

const BookingPage = () => {
  const user = useSelector((s) => s.user);
  const token = useSelector((s) => s.token);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const optionDetailRef = useRef(null);
  const lastScrolledOptionIdRef = useRef(null);
  const restoredDraftRef = useRef(false);
  const draftAutoSaveReadyRef = useRef(false);
  const draftAutoSaveTimerRef = useRef(null);
  const scrollToSlotsAfterDateSelectionRef = useRef(false);

  const params = new URLSearchParams(location.search);
  const initialRawOptionId = params.get("option") || null;
  const initialAudience = normalizeAudienceId(params.get("audience"));
  const initialOptionId = normalizeOptionId(initialRawOptionId);
  const initialWakeupComesToYou = wakeupComesToYouFromId(initialRawOptionId);
  const requestedOption = LEGACY_OPTION_IDS.has(initialOptionId)
    ? null
    : DELIVERY_STUDIO_OPTIONS[initialOptionId]
      ? optionForDelivery(initialOptionId, initialWakeupComesToYou)
      : ALL_OPTIONS.find((o) => o.id === initialOptionId && !o.externalUrl) ||
        null;
  const initialOption = requestedOption && isDeliveryChoiceOption(requestedOption)
    ? optionForDelivery(requestedOption, initialWakeupComesToYou)
    : requestedOption;
  const initialMonthlyActionRequested =
    params.get("monthlyAction") === "1" && isWakeupPlusOption(initialOption);
  const urlPersonCount = Number(params.get("persons"));
  const urlDuration = Number(params.get("duration"));
  const urlVoucherId = String(params.get("voucher") || "").trim();
  const initialPersonCount = initialMonthlyActionRequested
    ? MONTHLY_ACTION_VARIANTS[0].personCount
    : Number.isInteger(urlPersonCount) && urlPersonCount >= 1 && urlPersonCount <= 10
      ? urlPersonCount
      : defaultPersonCountForOption(initialOption);
  const initialDurationChoices = durationChoicesForOption(initialOption);
  const initialDuration = initialDurationChoices.includes(urlDuration)
    ? urlDuration
    : initialDurationMinutesForOption(initialOption);

  const [selectedOption, setSelectedOption] = useState(initialOption);
  const [openOfferDetails, setOpenOfferDetails] = useState(null);
  const slotsLoadingRef = useRef(false);
  const [wakeupComesToYou, setWakeupComesToYou] = useState(
    initialWakeupComesToYou
  );
  const [bookingAudience, setBookingAudience] = useState(() =>
    initialOption ? audienceForOption(initialOption) : initialAudience
  );
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [consultationNotes, setConsultationNotes] = useState("");
  // Address fields (mobile option).
  const [address, setAddress] = useState({
    companyName: "",
    fullAddress: "",
    street: "",
    postalCode: "",
    city: "",
    notes: "",
  });
  const [invoiceAddress, setInvoiceAddress] = useState({
    companyName: "",
    street: "",
    postalCode: "",
    city: "",
  });
  const invoicePrefillKeyRef = useRef("");
  const [invoiceSuggestions, setInvoiceSuggestions] = useState([]);
  const [profileAddressPrefilled, setProfileAddressPrefilled] = useState(false);
  const addressPrefillKeyRef = useRef("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [serviceAreaCheck, setServiceAreaCheck] = useState({
    status: "idle",
    message: "",
  });
  const lastCheckedAddressRef = useRef("");
  // Allowed PLZ prefixes (loaded on first mobile-option open).
  const [postalRules, setPostalRules] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(() =>
    defaultPaymentForOption(initialOption)
  );
  const [personCount, setPersonCount] = useState(initialPersonCount);
  const [monthlyActionVariant, setMonthlyActionVariant] = useState(
    initialMonthlyActionRequested ? MONTHLY_ACTION_VARIANTS[0].id : ""
  );
  const [monthlyActionUsed, setMonthlyActionUsed] = useState(token ? null : false);
  const monthlyAction = useMonthlyAction();
  const monthlyActionUnavailable = monthlyActionUsed === true || (Boolean(token) && monthlyActionUsed === null);
  useEffect(() => {
    if (!token || !isWakeupPlusOption(selectedOption)) {
      setMonthlyActionUsed(false);
      return undefined;
    }
    setMonthlyActionUsed(null);
    let cancelled = false;
    api.get("/bookings/monthly-action")
      .then(({ data }) => {
        if (!cancelled) setMonthlyActionUsed(Boolean(data?.used));
      })
      .catch(() => {
        if (!cancelled) setMonthlyActionUsed(false);
      });
    return () => { cancelled = true; };
  }, [token, selectedOption]);
  useEffect(() => {
    if (!monthlyActionUsed || !monthlyActionVariant) return;
    setMonthlyActionVariant("");
    setPersonCount(defaultPersonCountForOption(selectedOption));
    setSelectedSlot(null);
  }, [monthlyActionUsed, monthlyActionVariant, selectedOption]);
  const [durationMinutes, setDurationMinutes] = useState(initialDuration);
  const [redeemedVouchers, setRedeemedVouchers] = useState([]);
  const [selectedMassageVoucherId, setSelectedMassageVoucherId] = useState(urlVoucherId);
  useEffect(() => {
    if (!token) {
      setRedeemedVouchers([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get("/vouchers/mine")
      .then(({ data }) => {
        if (!cancelled) setRedeemedVouchers(data?.redeemed || []);
      })
      .catch(() => {
        if (!cancelled) setRedeemedVouchers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const [pricePreview, setPricePreview] = useState(() => {
    if (initialDurationChoices.length > 0 && !initialDuration) {
      return null;
    }
    return buildLocalPricePreview(
      initialOption,
      initialPersonCount,
      false,
      true,
      defaultPaymentForOption(initialOption),
      null,
      initialDuration || defaultDurationForOption(initialOption)
    );
  });
  const [loadingPricePreview, setLoadingPricePreview] = useState(false);
  const [hasVoucher, setHasVoucher] = useState(null);
  const [voucherTermsAccepted, setVoucherTermsAccepted] = useState(false);
  const [bookingPhone, setBookingPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [errorAction, setErrorAction] = useState(null);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [applyReferralBalance, setApplyReferralBalance] = useState(true);
  const [bookingConsents, setBookingConsents] = useState(initialBookingConsents);
  const [validationAttempted, setValidationAttempted] = useState(false);

  const userHasPhone = Boolean(String(user?.phone || "").trim());
  const needsBookingPhone = Boolean(user && selectedOption && !userHasPhone);
  const pricingUser = useMemo(
    () =>
      user && !String(user.phone || "").trim() && String(bookingPhone || "").trim()
        ? { ...user, phone: bookingPhone.trim() }
        : user,
    [bookingPhone, user]
  );
  const selectedMinDate = isLicensePartnerOption(selectedOption)
    ? licensePartnerMinDate()
    : new Date();
  const selectedMaxDate = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
  const appointmentIsArranged = skipsTimeslotSelection(selectedOption);
  const businessEventBookingClosed =
    isBusinessEventOption(selectedOption) && isAfterBusinessEventCutoff();
  const onlinePaymentOnly = requiresStripeOnlyPayment(selectedOption);
  const onsitePaymentDisabled = disablesOnsitePaymentChoice(selectedOption);
  const effectivePaymentMethod = onlinePaymentOnly
    ? "stripe"
    : paymentMethodForOption(selectedOption, paymentMethod);
  const selectedStudioLocation = studioLocationForOption(
    selectedOption,
    selectedDate
  );
  const personCountMin = Number(selectedOption?.personCountMin || 1);
  const personCountMax = Number.isFinite(Number(selectedOption?.personCountMax))
    ? Number(selectedOption.personCountMax)
    : null;
  const selectedPersonCount = Math.max(
    personCountMin,
    Math.min(
      personCountMax ?? Infinity,
      Math.round(Number(personCount || defaultPersonCountForOption(selectedOption)))
    )
  );
  const selectedDurationMinutes =
    Number(durationMinutes) > 0 ? Number(durationMinutes) : null;
  const durationChoices = useMemo(
    () => durationChoicesForOption(selectedOption),
    [selectedOption]
  );
  const showDurationChoice = durationChoices.length > 0;
  const requiresOnsiteAddress = Boolean(selectedOption?.requiresAddress);
  const selectedFullAddress = fullAddressFromServiceAddress(address);
  const requiresServiceAreaCheck =
    isDeliveryOnsiteOption(selectedOption) ||
    selectedOption?.offerType === "wakeup_plus_business";
  const requiresBusinessInvoice =
    isWakeupPlusOption(selectedOption) || isBusinessPlusOption(selectedOption);
  const requiresOnsiteBusinessIdentity =
    requiresBusinessInvoice && selectedOption?.bookingMode === "mobile";
  const wantsAddressSuggestions = requiresServiceAreaCheck;
  const addressIsEligible = serviceAreaCheck.status === "eligible";
  const matchingMassageVouchers = useMemo(() => {
    if (!selectedOption?.serviceKey) return [];
    return redeemedVouchers.filter((voucher) =>
      voucherMatchesSelection(voucher, {
        serviceKey: selectedOption.serviceKey,
        personCount: selectedPersonCount,
        durationMinutes: selectedDurationMinutes,
      })
    );
  }, [redeemedVouchers, selectedDurationMinutes, selectedOption, selectedPersonCount]);
  const selectedMassageVoucher =
    matchingMassageVouchers.find((voucher) => voucher.id === selectedMassageVoucherId) ||
    null;
  const voucherChoiceRequired = Boolean(selectedOption?.requiresVoucherQuestion);
  const voucherChoiceMissing =
    voucherChoiceRequired && hasVoucher === null && !selectedMassageVoucher;
  const durationChoiceMissing = showDurationChoice && !selectedDurationMinutes;

  const isPaidBooking =
    Boolean(selectedOption?.paidPackage) ||
    isStudioLikeBooking(selectedOption?.bookingMode) ||
    selectedOption?.bookingMode === "mobile";
  const voucherActive = isHoffmannVoucherActive(user, selectedOption);
  const selectedOptionId = selectedOption?.id;
  const isBusinessAudienceOption = audienceForOption(selectedOption) === AUDIENCE_BUSINESS;
  const showOnlineSavingsBadge = !isBusinessAudienceOption;
  const requiresPaymentChoiceBase =
    isPaidBooking && !voucherActive && !selectedOption?.paymentLocked;

  const hasDetailsStep =
    selectedOption?.bookingMode === "consultation" ||
    requiresOnsiteAddress ||
    isStudioLikeBooking(selectedOption?.bookingMode);
  const paidBasePriceCents = useMemo(() => {
    if (!isPaidBooking || !selectedOption) return 0;
    if (isBusinessEventOption(selectedOption)) {
      return (
        Number(selectedOption.unitPriceCents || selectedOption.priceCents || 0) *
        selectedPersonCount
      );
    }
    if (
      selectedOption.priceCents &&
      !isPremiumBookingOption(selectedOption) &&
      !isIntensePlusBusinessOption(selectedOption)
    ) {
      return selectedOption.priceCents;
    }
    if (isPremiumBookingOption(selectedOption) && !selectedDurationMinutes) {
      return selectedOption.priceCents || 7000;
    }
    const service = DR_HOFFMANN_SERVICES.find(
      (s) => s.key === selectedOption.serviceKey
    );
    return (
      individualServicePriceForPaymentMethod(
        selectedOption.serviceKey,
        effectivePaymentMethod,
        selectedDurationMinutes
      ) ??
      service?.durations.find(
        (duration) =>
          Number(duration.minutes) === Number(selectedDurationMinutes)
      )?.priceCents ??
      service?.durations[0]?.priceCents ??
      0
    );
  }, [isPaidBooking, selectedOption, selectedPersonCount, selectedDurationMinutes, effectivePaymentMethod]);

  const fixedGrossPricePreview = useMemo(() => {
    if (!isFixedGrossIndividualOption(selectedOption)) return null;
    if (selectedOption?.requiresVoucherQuestion && hasVoucher === null && !selectedMassageVoucher) {
      return null;
    }
    if (showDurationChoice && !selectedDurationMinutes) {
      return null;
    }
    return buildLocalPricePreview(
      selectedOption,
      selectedPersonCount,
      Boolean(hasVoucher),
      true,
      effectivePaymentMethod,
      pricingUser,
      selectedDurationMinutes
    );
  }, [selectedOption, selectedPersonCount, selectedDurationMinutes, showDurationChoice, hasVoucher, effectivePaymentMethod, pricingUser, selectedMassageVoucher]);
  const currentPricePreview = fixedGrossPricePreview || pricePreview;

  const effectivePriceCents = isPaidBooking
    ? durationChoiceMissing
      ? null
      : currentPricePreview?.grossCents
      ? currentPricePreview.grossCents
      : voucherActive
      ? 0
      : priceForPaymentMethod(paidBasePriceCents, effectivePaymentMethod, {
          serviceKey: selectedOption?.serviceKey,
          user: pricingUser,
          durationMinutes: selectedDurationMinutes,
        })
    : 0;
  const referralBalanceCents = Math.max(0, Number(user?.referralBalanceCents || 0));
  const canUseReferralBalance =
    Boolean(user) &&
    referralBalanceCents > 0 &&
    isPaidBooking &&
    !voucherActive &&
    !selectedMassageVoucher &&
    isReferralBalanceEligibleOption(selectedOption);
  const grossBeforeReferralCents = selectedMassageVoucher
    ? 0
    : (currentPricePreview?.grossCents ?? effectivePriceCents);
  const referralCreditPreviewCents =
    canUseReferralBalance && applyReferralBalance
      ? Math.min(referralBalanceCents, Math.max(0, grossBeforeReferralCents))
      : 0;
  const payableAfterReferralCents = Math.max(
    0,
    grossBeforeReferralCents - referralCreditPreviewCents
  );
  const isFreeDueToReferralBalance =
    applyReferralBalance &&
    referralCreditPreviewCents > 0 &&
    payableAfterReferralCents === 0;
  const showPaymentMethodChoice =
    requiresPaymentChoiceBase && payableAfterReferralCents > 0;
  const showPaymentStep =
    isPaidBooking &&
    !voucherActive &&
    grossBeforeReferralCents > 0 &&
    (showPaymentMethodChoice || canUseReferralBalance);
  const summaryStepNumber = showPaymentStep
    ? hasDetailsStep
      ? "04"
      : "03"
    : hasDetailsStep
      ? "03"
      : "02";
  const packageVatCents = selectedOption?.priceNetCents
    ? selectedOption.priceCents - selectedOption.priceNetCents
    : 0;
  const includedPrivateVatCents =
    isFixedGrossIndividualOption(selectedOption) && currentPricePreview
      ? currentPricePreview.grossCents -
        Math.round(currentPricePreview.grossCents / (1 + VAT_RATE))
      : null;

  const allowedPostalCodes = useMemo(() => {
    const codes = postalRules?.allowedPostalCodes || postalRules?.allowedPrefixes;
    return new Set(Array.isArray(codes) ? codes : []);
  }, [postalRules]);

  const plzIsValid =
    /^\d{5}$/.test(address.postalCode) && allowedPostalCodes.has(address.postalCode);

  const missingFields = [];
  const requireField = (missing, id, label) => {
    if (missing) missingFields.push({ id, label });
  };
  if (selectedOption) {
    if (!appointmentIsArranged) {
      requireField(!selectedDate, "booking-date", "Datum auswählen");
      requireField(Boolean(selectedDate) && !selectedSlot, "booking-slot", "Uhrzeit auswählen");
    }
    requireField(durationChoiceMissing, "duration-minutes", "Dauer auswählen");
    requireField(voucherChoiceMissing, "has-voucher", "Gutscheinfrage beantworten");
    requireField(hasVoucher === true && !voucherTermsAccepted, "voucher-terms", "Gutscheinmitnahme bestätigen");
    if (requiresOnsiteBusinessIdentity) {
      requireField(!String(address.companyName || "").trim(), "ad-company", "Name oder Firma am Einsatzort eingeben");
    }
    if (selectedOption.bookingMode === "mobile") {
      if (selectedOption.usesLegacyAddressFields) {
        requireField(!String(address.street || "").trim(), "ad-street", "Straße und Hausnummer eingeben");
        requireField(!String(address.postalCode || "").trim(), "ad-plz", "PLZ eingeben");
        requireField(!String(address.city || "").trim(), "ad-city", "Ort eingeben");
      } else {
        requireField(!selectedFullAddress, "ad-full-address", "Massageadresse eingeben");
      }
      requireField(Boolean(selectedFullAddress) && requiresServiceAreaCheck && !addressIsEligible, "ad-full-address", "Massageadresse prüfen");
      requireField(!selectedOption.skipPostalCheck && Boolean(address.postalCode) && !plzIsValid, "ad-plz", "Hamburger PLZ prüfen");
    }
    if (requiresBusinessInvoice) {
      requireField(!String(invoiceAddress.companyName || "").trim(), "invoice-company", "Unternehmensnamen für die Rechnung eingeben");
      requireField(!String(invoiceAddress.street || "").trim(), "invoice-street", "Straße und Hausnummer der Rechnungsadresse eingeben");
      requireField(!String(invoiceAddress.postalCode || "").trim(), "invoice-plz", "PLZ der Rechnungsadresse eingeben");
      requireField(!String(invoiceAddress.city || "").trim(), "invoice-city", "Ort der Rechnungsadresse eingeben");
    }
    requireField(!bookingConsents.legalTerms, "booking-terms", "AGB und Datenschutzhinweise bestätigen");
  }
  const missingFieldIds = new Set(missingFields.map(({ id }) => id));
  const invalidFieldProps = (id) =>
    validationAttempted && missingFieldIds.has(id)
      ? { "aria-invalid": true, className: "booking-field-invalid" }
      : {};

  useEffect(() => {
    if (!user || selectedOption?.bookingMode !== "mobile") return;
    const prefill = addressPrefillFromUser(user);
    const hasStoredAddress = hasAddressDetails(prefill);
    const prefillKey = [
      user._id || user.email || "user",
      selectedOption.id,
      hasStoredAddress
        ? prefill.fullAddress || prefill.street || prefill.postalCode || "address"
        : "name-only",
    ].join(":");
    if (addressPrefillKeyRef.current === prefillKey) return;
    if (hasStoredAddress) setProfileAddressPrefilled(true);

    setAddress((current) => {
      const hasCurrentAddress = hasAddressDetails(current);
      const currentIsNameOnly = addressLooksLikeCustomerName(current, user);
      const currentCompanyName = String(current.companyName || "").trim();

      if (hasCurrentAddress && !currentIsNameOnly) {
        if (!currentCompanyName && prefill.companyName) {
          addressPrefillKeyRef.current = prefillKey;
          return { ...current, companyName: prefill.companyName };
        }
        return current;
      }

      if (!hasStoredAddress && !prefill.companyName) return current;
      addressPrefillKeyRef.current = prefillKey;
      return { ...current, ...prefill };
    });
  }, [selectedOption, user]);

  useEffect(() => {
    if (!requiresBusinessInvoice || !user?.rechnungAddress) return;
    const stored = user.rechnungAddress;
    const parsed = splitInvoiceAddressLine(stored.fullAddress || stored.street || "");
    const prefillKey = [
      user._id || user.email || "user",
      stored.companyName || "",
      stored.fullAddress || "",
      stored.street || "",
      stored.postalCode || "",
      stored.city || "",
    ].join(":");
    if (invoicePrefillKeyRef.current === prefillKey) return;
    setInvoiceAddress((current) => {
      const hasCurrent =
        current.companyName || current.street || current.postalCode || current.city;
      if (hasCurrent) {
        if (!String(current.companyName || "").trim() && stored.companyName) {
          invoicePrefillKeyRef.current = prefillKey;
          return { ...current, companyName: String(stored.companyName).trim() };
        }
        return current;
      }
      invoicePrefillKeyRef.current = prefillKey;
      return {
        companyName: String(stored.companyName || "").trim(),
        street: String(stored.street && stored.street !== stored.fullAddress ? stored.street : parsed.street).trim(),
        postalCode: String(stored.postalCode || parsed.postalCode || "").trim(),
        city: String(stored.city || parsed.city || "").trim(),
      };
    });
  }, [requiresBusinessInvoice, user]);

  const personCountChoices = useMemo(() => {
    if (!selectedOption?.personCountMax) return [];
    const min = Number(selectedOption.personCountMin || 1);
    const max = Number(selectedOption.personCountMax || min);
    return Array.from({ length: Math.max(0, max - min + 1) }, (_, idx) => min + idx);
  }, [selectedOption]);
  const usesPersonCountCounter = isWakeupPlusOption(selectedOption);
  const showPersonCount =
    usesPersonCountCounter ||
    personCountChoices.length > 0 ||
    Boolean(selectedOption?.personCountDefault);
  const personCountLabel = isWakeupPlusOption(selectedOption)
    ? "Mitarbeitende"
    : isBusinessEventOption(selectedOption)
      ? "Plätze"
      : "Personen";
  const updatePersonCount = useCallback(
    (next) => {
      const parsed = Math.round(Number(next));
      const fallback = defaultPersonCountForOption(selectedOption);
      const upper = personCountMax ?? Infinity;
      const bounded = Math.min(
        upper,
        Math.max(personCountMin, Number.isFinite(parsed) ? parsed : fallback)
      );
      setPersonCount(bounded);
      setSelectedSlot(null);
      scrollToSlotsAfterDateSelectionRef.current = false;
    },
    [personCountMax, personCountMin, selectedOption]
  );
  const renderPersonCountInput = useCallback(
    (id) =>
      usesPersonCountCounter ? (
        <PersonCountCounter
          id={id}
          value={selectedPersonCount}
          min={personCountMin}
          max={personCountMax ?? undefined}
          onChange={updatePersonCount}
        />
      ) : (
        <select
          id={id}
          value={selectedPersonCount}
          onChange={(e) => updatePersonCount(e.target.value)}
        >
          {personCountChoices.map((count) => (
            <option key={count} value={count}>
              {count}
            </option>
          ))}
        </select>
      ),
    [
      personCountChoices,
      personCountMax,
      personCountMin,
      selectedPersonCount,
      updatePersonCount,
      usesPersonCountCounter,
    ]
  );

  const persistDraft = useCallback(() => {
    if (!selectedOption) return;
    saveBookingDraft(
      draftFromState({
        selectedOption,
        selectedDate,
        selectedSlot,
        consultationNotes,
        address,
        invoiceAddress,
        serviceAreaCheck,
        paymentMethod: effectivePaymentMethod,
        personCount,
        durationMinutes: selectedDurationMinutes,
        hasVoucher,
        voucherTermsAccepted,
        bookingPhone,
        wakeupComesToYou,
        bookingConsents,
        applyReferralBalance,
        selectedMassageVoucherId,
        monthlyActionVariant,
      })
    );
  }, [
    selectedOption,
    selectedDate,
    selectedSlot,
    consultationNotes,
    address,
    invoiceAddress,
    serviceAreaCheck,
    effectivePaymentMethod,
    personCount,
    selectedDurationMinutes,
    hasVoucher,
    voucherTermsAccepted,
    bookingPhone,
    wakeupComesToYou,
    bookingConsents,
    applyReferralBalance,
    selectedMassageVoucherId,
    monthlyActionVariant,
  ]);

  const scrollToOptionDetail = useCallback(() => {
    const target =
      optionDetailRef.current?.querySelector(".booking-step") ||
      optionDetailRef.current;
    if (!target) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    target.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "center",
    });
  }, []);

  const scrollBookingTargetToCenter = useCallback((target) => {
    if (!target) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    target.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "center",
    });
  }, []);

  const scrollToMonthlyAction = useCallback(() => {
    const target = optionDetailRef.current?.querySelector(".booking-monthly-action");
    scrollBookingTargetToCenter(target);
  }, [scrollBookingTargetToCenter]);

  const scrollToNextBookingStep = useCallback((source) => {
    const currentStep = source?.closest?.(".booking-step");
    const root = optionDetailRef.current;
    if (!currentStep || !root) return;
    const steps = Array.from(root.querySelectorAll(".booking-step"));
    const currentIndex = steps.indexOf(currentStep);
    const nextStep = steps
      .slice(currentIndex + 1)
      .find((step) => step.getClientRects().length > 0);
    if (nextStep) {
      window.requestAnimationFrame(() => scrollBookingTargetToCenter(nextStep));
    }
  }, [scrollBookingTargetToCenter]);

  const scrollToNextRequiredBookingField = useCallback((source) => {
    const currentStep = source?.closest?.(".booking-step");
    const root = optionDetailRef.current;
    if (!currentStep || !root) return;

    if (source.id === "duration-minutes") {
      const calendar = currentStep.querySelector(".booking-calendar-target");
      window.requestAnimationFrame(() => scrollBookingTargetToCenter(calendar));
      return;
    }

    if (source.id === "ad-full-address") return;
    if (source.id === "ad-city" && !plzIsValid) return;

    const labelFor = (control) => {
      const voucherLabel = control.closest(".voucher-question")?.querySelector(
        ".voucher-question__label"
      );
      if (voucherLabel) return voucherLabel.textContent || "";
      const label = control.id
        ? root.querySelector(`label[for="${control.id}"]`)
        : null;
      return label?.textContent || control.closest("label")?.textContent || "";
    };
    const isVisible = (element) => element.getClientRects().length > 0;
    const controls = Array.from(
      currentStep.querySelectorAll("input, select, textarea")
    ).filter((control) => isVisible(control) && !control.disabled);
    const sourceIndex = controls.indexOf(source);
    const nextRequired = controls
      .slice(Math.max(0, sourceIndex + 1))
      .find((control) => {
        if (source.type === "radio" && control.name === source.name) return false;
        return labelFor(control).includes("*");
      });

    const nextTarget = nextRequired
      ? nextRequired.closest(".full") ||
        nextRequired.closest(".address-grid > div") ||
        nextRequired.closest(".voucher-question") ||
        nextRequired.closest("label") ||
        nextRequired
      : currentStep.querySelector(".booking-calendar-target");
    window.requestAnimationFrame(() => {
      if (nextTarget) {
        scrollBookingTargetToCenter(nextTarget);
      } else {
        scrollToNextBookingStep(source);
      }
    });
  }, [plzIsValid, scrollBookingTargetToCenter, scrollToNextBookingStep]);

  const handleBookingFieldChangeCapture = useCallback((event) => {
    const source = event.target;
    if (!(source instanceof HTMLInputElement || source instanceof HTMLSelectElement)) return;
    if (source.id.startsWith("person-count-")) return;
    if (source.type === "radio" && !source.checked) return;
    if (source.type === "checkbox" && !source.checked) return;
    if (source.id === "duration-minutes") {
      scrollToNextRequiredBookingField(source);
      return;
    }
    if (source.closest(".booking-delivery-voucher .booking-checkbox")) {
      window.requestAnimationFrame(() => {
        scrollBookingTargetToCenter(
          optionDetailRef.current?.querySelector(".booking-calendar-target")
        );
      });
      return;
    }
    if (source.name === "has-voucher") {
      if (source.value === "yes" && source.checked) {
        window.requestAnimationFrame(() => {
          scrollBookingTargetToCenter(
            optionDetailRef.current?.querySelector(".booking-checkbox")
          );
        });
      } else {
        scrollToNextRequiredBookingField(source);
      }
      return;
    }
    if (source.closest(".booking-step")) {
      scrollToNextRequiredBookingField(source);
    }
  }, [scrollBookingTargetToCenter, scrollToNextRequiredBookingField]);

  const handleBookingFieldBlurCapture = useCallback((event) => {
    const source = event.target;
    if (!(source instanceof HTMLInputElement || source instanceof HTMLTextAreaElement)) return;
    if (event.relatedTarget?.closest?.(".person-counter")) return;
    if (source.id.startsWith("person-count-")) return;
    if (source.type === "radio" || source.type === "checkbox" || !source.value.trim()) return;
    if (source.id === "ad-full-address") {
      if (!requiresServiceAreaCheck && selectedFullAddress.trim()) {
        scrollToNextBookingStep(source);
      }
      return;
    }
    if (source.id === "ad-city" && !plzIsValid) return;
    const label = source.id
      ? optionDetailRef.current?.querySelector(`label[for="${source.id}"]`)
      : source.closest("label");
    if (label?.textContent?.toLowerCase().includes("optional")) return;
    scrollToNextRequiredBookingField(source);
  }, [
    plzIsValid,
    requiresServiceAreaCheck,
    scrollToNextBookingStep,
    scrollToNextRequiredBookingField,
    selectedFullAddress,
  ]);

  useEffect(() => {
    if (restoredDraftRef.current) return;
    restoredDraftRef.current = true;
    if (urlVoucherId || initialMonthlyActionRequested) return;
    const draft = readBookingDraft();
    if (!draft) return;

    const normalizedDraftOptionId = normalizeOptionId(draft.optionId);
    if (initialOption && normalizedDraftOptionId !== initialOption.id) return;
    const draftWakeupComesToYou = Boolean(
      draft.wakeupComesToYou || wakeupComesToYouFromId(draft.optionId)
    );
    const option = LEGACY_OPTION_IDS.has(normalizedDraftOptionId)
      ? null
      : DELIVERY_STUDIO_OPTIONS[normalizedDraftOptionId]
        ? optionForDelivery(normalizedDraftOptionId, draftWakeupComesToYou)
        : ALL_OPTIONS.find((o) => o.id === normalizedDraftOptionId) ||
          initialOption;
    if (option) {
      setSelectedOption(option);
      setBookingAudience(audienceForOption(option));
      if (isDeliveryChoiceOption(option)) setWakeupComesToYou(draftWakeupComesToYou);
    }
    if (draft.selectedDate) {
      const [year, month, day] = String(draft.selectedDate).split("-").map(Number);
      if (year && month && day) setSelectedDate(new Date(year, month - 1, day));
    }
    if (draft.selectedSlot) setSelectedSlot(draft.selectedSlot);
    if (typeof draft.consultationNotes === "string") {
      setConsultationNotes(draft.consultationNotes);
    }
    if (draft.address && typeof draft.address === "object") {
      setAddress((current) => ({ ...current, ...draft.address }));
    }
    if (draft.invoiceAddress && typeof draft.invoiceAddress === "object") {
      setInvoiceAddress((current) => ({ ...current, ...draft.invoiceAddress }));
    }
    if (draft.serviceAreaCheck && typeof draft.serviceAreaCheck === "object") {
      setServiceAreaCheck((current) => ({ ...current, ...draft.serviceAreaCheck }));
    }
    if (draft.paymentMethod) {
      setPaymentMethod(paymentMethodForOption(option, draft.paymentMethod));
    }
    if (draft.personCount) setPersonCount(Number(draft.personCount));
    if (draft.durationMinutes) setDurationMinutes(Number(draft.durationMinutes));
    if (draft.hasVoucher === true || draft.hasVoucher === false) {
      setHasVoucher(draft.hasVoucher);
    }
    setVoucherTermsAccepted(Boolean(draft.voucherTermsAccepted));
    if (draft.bookingPhone) setBookingPhone(draft.bookingPhone);
    if (draft.bookingConsents && typeof draft.bookingConsents === "object") {
      setBookingConsents({ legalTerms: Boolean(draft.bookingConsents.legalTerms) });
    }
    if (typeof draft.applyReferralBalance === "boolean") {
      setApplyReferralBalance(draft.applyReferralBalance);
    }
    if (draft.selectedMassageVoucherId) {
      setSelectedMassageVoucherId(draft.selectedMassageVoucherId);
    }
    if (option && isWakeupPlusOption(option)) {
      const variant = MONTHLY_ACTION_VARIANTS.find((item) => item.id === draft.monthlyActionVariant);
      if (variant) {
        setMonthlyActionVariant(variant.id);
        setPersonCount(variant.personCount);
      }
    }
  }, [initialOption, initialMonthlyActionRequested, urlVoucherId]);

  useEffect(() => {
    // Defer auto-save until restoration has committed, including React's
    // development StrictMode replay of the mount effects.
    if (!draftAutoSaveReadyRef.current) {
      if (draftAutoSaveTimerRef.current === null) {
        draftAutoSaveTimerRef.current = window.setTimeout(() => {
          draftAutoSaveReadyRef.current = true;
          draftAutoSaveTimerRef.current = null;
        }, 0);
      }
      return;
    }
    persistDraft();
  }, [persistDraft]);

  useEffect(() => {
    if (!selectedOptionId || lastScrolledOptionIdRef.current === selectedOptionId) return;
    lastScrolledOptionIdRef.current = selectedOptionId;
    window.requestAnimationFrame(
      initialMonthlyActionRequested ? scrollToMonthlyAction : scrollToOptionDetail
    );
  }, [selectedOptionId, initialMonthlyActionRequested, scrollToMonthlyAction, scrollToOptionDetail]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (!cancelled && data?.user) dispatch(setUser({ user: data.user }));
      })
      .catch(() => {
        if (!cancelled) dispatch(setLogout());
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, token]);

  useEffect(() => {
    if (!selectedOption?.offerType) {
      setPricePreview(null);
      setLoadingPricePreview(false);
      return undefined;
    }
    if (isBusinessEventOption(selectedOption)) {
      setPricePreview(
        buildLocalPricePreview(
          selectedOption,
          selectedPersonCount,
          false,
          true,
          effectivePaymentMethod,
          pricingUser
        )
      );
      setLoadingPricePreview(false);
      return undefined;
    }
    if (selectedOption.requiresVoucherQuestion && hasVoucher === null && !selectedMassageVoucher) {
      setPricePreview(null);
      setLoadingPricePreview(false);
      return undefined;
    }
    if (showDurationChoice && !selectedDurationMinutes) {
      setPricePreview(null);
      setLoadingPricePreview(false);
      return undefined;
    }
    const localPreview = buildLocalPricePreview(
      selectedOption,
      selectedPersonCount,
      Boolean(hasVoucher),
      true,
      effectivePaymentMethod,
      pricingUser,
      selectedDurationMinutes
    );
    setPricePreview(localPreview);
    let cancelled = false;
    setLoadingPricePreview(true);

    api
      .post("/bookings/price-preview", {
        brand: PRICE_PREVIEW_BRAND,
        serviceKey: selectedOption.serviceKey,
        offerType: selectedOption.offerType,
        personCount: selectedPersonCount,
        durationMinutes: selectedDurationMinutes,
        ...(isWakeupPlusOption(selectedOption) && monthlyActionVariant
          ? { monthlyActionVariant }
          : {}),
        paymentMethod: effectivePaymentMethod,
        ...(selectedOption.requiresVoucherQuestion
          ? { hasVoucher: Boolean(hasVoucher) }
          : {}),
        ...(isLicensePartnerOption(selectedOption)
          ? { licensePartnerLocation: selectedOption.licensePartnerLocation }
          : {}),
      })
      .then(({ data }) => {
        const backendPreview = isFixedGrossIndividualOption(selectedOption)
          ? localPreview
          : normalizePricePreview(data);
        if (!cancelled && backendPreview) setPricePreview(backendPreview);
      })
      .catch(() => {
        // Backend is the source of truth. This client formula is only a temporary
        // display fallback until the shared API exposes price-preview everywhere.
        if (!cancelled) setPricePreview(localPreview);
      })
      .finally(() => {
        if (!cancelled) setLoadingPricePreview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedOption, selectedPersonCount, selectedDurationMinutes, showDurationChoice, hasVoucher, effectivePaymentMethod, pricingUser, selectedMassageVoucher, monthlyActionVariant]);

  useEffect(() => {
    if (!wantsAddressSuggestions || selectedFullAddress.length < 3) {
      setAddressSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .get("/service-area/address-suggestions", {
          params: {
            brand: PRICE_PREVIEW_BRAND,
            q: selectedFullAddress,
          },
        })
        .then(({ data }) => {
          if (cancelled) return;
          const suggestions = Array.isArray(data?.suggestions)
            ? data.suggestions
            : Array.isArray(data)
              ? data
              : [];
          setAddressSuggestions(suggestions.slice(0, 5));
        })
        .catch(() => {
          if (!cancelled) setAddressSuggestions([]);
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [wantsAddressSuggestions, selectedFullAddress]);

  useEffect(() => {
    const query = String(invoiceAddress.street || "").trim();
    if (!requiresBusinessInvoice || query.length < 3) {
      setInvoiceSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .get("/service-area/address-suggestions", {
          params: { brand: PRICE_PREVIEW_BRAND, q: query },
        })
        .then(({ data }) => {
          if (cancelled) return;
          const suggestions = Array.isArray(data?.suggestions)
            ? data.suggestions
            : Array.isArray(data)
              ? data
              : [];
          setInvoiceSuggestions(suggestions.slice(0, 5));
        })
        .catch(() => {
          if (!cancelled) setInvoiceSuggestions([]);
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [invoiceAddress.street, requiresBusinessInvoice]);

  // Lazy-load postal rules whenever the user opens the mobile flow.
  useEffect(() => {
    if (selectedOption?.bookingMode !== "mobile") return undefined;
    if (selectedOption?.skipPostalCheck) return undefined;
    if (postalRules) return undefined;
    let cancelled = false;
    api
      .get("/bookings/postal-rules")
      .then(({ data }) => {
        if (!cancelled && (data?.allowedPostalCodes || data?.allowedPrefixes)) {
          setPostalRules(data);
        }
      })
      .catch(() => {
        if (!cancelled) setPostalRules({ allowedPostalCodes: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedOption, postalRules]);

  // Reload slots when the date or option changes.
  useEffect(() => {
    if (appointmentIsArranged) {
      setSlots([]);
      setSelectedSlot(null);
      return undefined;
    }
    if (!selectedOption || !selectedDate) {
      setSlots([]);
      setAvailabilityError(null);
      return undefined;
    }
    if (showDurationChoice && !selectedDurationMinutes) {
      setSlots([]);
      setSelectedSlot(null);
      setAvailabilityError("Bitte wähle zuerst eine Dauer aus.");
      return undefined;
    }
    let cancelled = false;
    setLoadingSlots(true);
    setAvailabilityError(null);
    const availabilityDuration = isWakeupBookingOption(selectedOption)
      ? wakeupServiceDurationMinutes(selectedOption, selectedPersonCount)
      : selectedDurationMinutes;
    const availabilityPersonCount = isWakeupPlusOption(selectedOption)
      ? Math.min(selectedPersonCount, 10)
      : selectedPersonCount;
    const availabilityParams = {
      date: toISODate(selectedDate),
      durationMinutes: availabilityDuration,
      brand: "dr-hoffmann",
      serviceKey: availabilityServiceKeyForOption(selectedOption),
      minimumLeadMinutes: selectedOption.minimumLeadMinutes,
    };
    const slotStepMinutes = slotStepMinutesForOption(selectedOption);
    if (slotStepMinutes) {
      availabilityParams.slotStepMinutes = slotStepMinutes;
    }
    if (isWakeupBookingOption(selectedOption)) {
      availabilityParams.personCount = availabilityPersonCount;
    }
    if (isWakeupPlusOption(selectedOption) && monthlyActionVariant) {
      availabilityParams.monthlyActionVariant = monthlyActionVariant;
    }
    if (selectedOption.hoffmannBookingOption) {
      availabilityParams.hoffmannBookingOption = selectedOption.hoffmannBookingOption;
    }
    if (
      selectedOption.bookingMode === "studio-weekday" &&
      !usesDailyHoffmannSchedule(selectedOption)
    ) {
      availabilityParams.studioSchedule = "weekday";
      availabilityParams.location = studioLocationForOption(
        selectedOption,
        selectedDate
      );
    } else if (selectedOption.bookingMode === "studio-weekday") {
      availabilityParams.studioSchedule = studioScheduleForOption(
        selectedOption,
        selectedDate
      );
      availabilityParams.location = studioLocationForOption(
        selectedOption,
        selectedDate
      );
    } else if (selectedOption.bookingMode === "studio") {
      availabilityParams.studioSchedule = "thursday";
      availabilityParams.location = studioLocationForOption(
        selectedOption,
        selectedDate
      );
    }
    api
      .get("/bookings/availability", {
        params: availabilityParams,
      })
      .then(({ data }) => {
        if (cancelled) return;
        const rawSlots = Array.isArray(data?.slots) ? data.slots : [];
        const availableSlots = filterSlotsForOption(selectedOption, selectedDate, rawSlots);
        setSlots(availableSlots);
        setSelectedSlot((current) => availableSlots.includes(current) ? current : null);
      })
      .catch((err) => {
        if (!cancelled) {
          const responseMessage = String(err?.response?.data?.message || "").toLowerCase();
          let message = null;
          if (/2 stunden|mindestens.*2 stunden|2h|2 h|minimum.*2 stunden/i.test(responseMessage)) {
            message = `Bitte wähle einen Termin mit mindestens ${
              selectedOption.minimumLeadMinutes || 120
            } Minuten Vorlauf.`;
          } else if (/10:00|22:00|zwischen 10:00 und 22:00/i.test(responseMessage)) {
            message = "Bitte w\u00e4hle eine Startzeit zwischen 10:00 und 22:00 Uhr.";
          } else if (/12:00|18:00|19:00|19:30|20:00|zwischen 12:00 und 19:30|zwischen 12:00 und 19:00|zwischen 12:00 und 20:00|startzeit/i.test(responseMessage)) {
            message = usesIndividualSchedule(selectedOption)
              ? "Bitte w\u00e4hle eine Startzeit zwischen 10:00 und 22:00 Uhr."
              : "Bitte w\u00e4hle eine Startzeit zwischen 12:00 und 19:30 Uhr.";
          }
          setAvailabilityError(
            message ||
              "Wir konnten keine passenden Termine laden. Bitte wähle einen späteren Zeitpunkt."
          );
          setSlots([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentIsArranged, selectedDate, selectedOption, selectedPersonCount, selectedDurationMinutes, showDurationChoice, requiresServiceAreaCheck, monthlyActionVariant]);

  useEffect(() => {
    if (loadingSlots) {
      slotsLoadingRef.current = true;
      return;
    }
    if (!slotsLoadingRef.current) return;
    slotsLoadingRef.current = false;
    if (!scrollToSlotsAfterDateSelectionRef.current) return;
    scrollToSlotsAfterDateSelectionRef.current = false;
    if (!selectedDate || slots.length === 0) return;
    const slotGrid = optionDetailRef.current?.querySelector(".slot-grid");
    window.requestAnimationFrame(() => scrollBookingTargetToCenter(slotGrid));
  }, [loadingSlots, selectedDate, slots, scrollBookingTargetToCenter]);

  const handleSelectOption = (option, { monthlyAction = false } = {}) => {
    const nextOption = isDeliveryChoiceOption(option)
      ? optionForDelivery(option, false)
      : option;
    if (isBusinessEventOption(nextOption) && isAfterBusinessEventCutoff()) {
      setError(
        "Die Buchung ist nur bis Sonntag, 14.06.2026 um 16:00 Uhr möglich."
      );
      setErrorAction(null);
      return;
    }
    setBookingAudience(
      isBusinessEventOption(nextOption)
        ? bookingAudience
        : audienceForOption(nextOption)
    );
    if (option.specialRequestPath) {
      navigate(option.specialRequestPath);
      return;
    }
    if (option.externalUrl) {
      window.open(option.externalUrl, "_blank", "noopener,noreferrer");
      return;
    }
    setSelectedOption(nextOption);
    scrollToSlotsAfterDateSelectionRef.current = false;
    if (isDeliveryChoiceOption(nextOption)) setWakeupComesToYou(false);
    setSelectedDate(null);
    setSelectedSlot(null);
    setPaymentMethod(defaultPaymentForOption(nextOption));
    const selectMonthlyAction = monthlyAction && isWakeupPlusOption(nextOption);
    setPersonCount(selectMonthlyAction
      ? MONTHLY_ACTION_VARIANTS[0].personCount
      : defaultPersonCountForOption(nextOption));
    setMonthlyActionVariant(selectMonthlyAction ? MONTHLY_ACTION_VARIANTS[0].id : "");
    setDurationMinutes(initialDurationMinutesForOption(nextOption));
    setHasVoucher(nextOption.requiresVoucherQuestion ? null : false);
    setVoucherTermsAccepted(false);
    setBookingConsents(initialBookingConsents);
    setServiceAreaCheck({ status: "idle", message: "" });
    lastCheckedAddressRef.current = "";
    setAddressSuggestions([]);
    setError(null);
    setErrorAction(null);
    setValidationAttempted(false);
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("option", nextOption.id);
    nextParams.set("audience", audienceForOption(nextOption));
    if (selectMonthlyAction) nextParams.set("monthlyAction", "1");
    else nextParams.delete("monthlyAction");
    navigate(`/buchen?${nextParams.toString()}`, { replace: true });
    window.setTimeout(selectMonthlyAction ? scrollToMonthlyAction : scrollToOptionDetail, 0);
  };

  const handleWakeupDeliveryChange = (comesToYou) => {
    const nextOption = optionForDelivery(selectedOption, comesToYou);
    if (!nextOption) return;
    setWakeupComesToYou(comesToYou);
    setSelectedOption(nextOption);
    scrollToSlotsAfterDateSelectionRef.current = false;
    setSelectedDate(null);
    setSelectedSlot(null);
    setPaymentMethod(defaultPaymentForOption(nextOption));
    setPersonCount(defaultPersonCountForOption(nextOption));
    setMonthlyActionVariant("");
    setDurationMinutes(initialDurationMinutesForOption(nextOption));
    setHasVoucher(nextOption.requiresVoucherQuestion ? null : false);
    setVoucherTermsAccepted(false);
    setBookingConsents(initialBookingConsents);
    setServiceAreaCheck({ status: "idle", message: "" });
    lastCheckedAddressRef.current = "";
    setAddressSuggestions([]);
    setError(null);
    setErrorAction(null);
    setValidationAttempted(false);
  };

  const handleAudienceChange = (audienceId) => {
    setBookingAudience(audienceId);
    const nextParams = new URLSearchParams(location.search);
    if (selectedOption && audienceForOption(selectedOption) !== audienceId) {
      setSelectedOption(null);
      setMonthlyActionVariant("");
      scrollToSlotsAfterDateSelectionRef.current = false;
      setSelectedDate(null);
      setSelectedSlot(null);
      setError(null);
      setErrorAction(null);
      setValidationAttempted(false);
      nextParams.delete("option");
      nextParams.delete("monthlyAction");
    }
    if (audienceId === AUDIENCE_BUSINESS) {
      nextParams.set("audience", audienceId);
    } else {
      nextParams.delete("audience");
    }
    const query = nextParams.toString();
    navigate(query ? `/buchen?${query}` : "/buchen", { replace: true });
  };

  const visibleOptions = (
    bookingAudience === AUDIENCE_BUSINESS
      ? BUSINESS_OPTIONS
      : [
          WAKEUP_STUDIO_OPTION,
          CLASSIC_STUDIO_OPTION,
          PREMIUM_STUDIO_OPTION,
          OPTIONS.find((option) => option.id === "studio"),
        ].filter(Boolean)
  ).filter(
    (option) => !isBusinessEventOption(option) || !isAfterBusinessEventCutoff()
  );
  const activeAudience =
    BOOKING_AUDIENCES.find((audience) => audience.id === bookingAudience) ||
    BOOKING_AUDIENCES[0];

  const updateAddress = (key) => (e) => {
    const value = e.target.value;
    setProfileAddressPrefilled(false);
    setAddress((prev) => ({ ...prev, [key]: value }));
    if (
      requiresServiceAreaCheck &&
      ["fullAddress", "street", "postalCode", "city"].includes(key)
    ) {
      setServiceAreaCheck({ status: "idle", message: "" });
      lastCheckedAddressRef.current = "";
    }
  };

  const updateInvoiceAddress = (key) => (e) => {
    const value = e.target.value;
    setInvoiceAddress((prev) => ({ ...prev, [key]: value }));
  };

  const applyInvoiceSuggestion = (suggestion) => {
    const value =
      typeof suggestion === "string"
        ? suggestion
        : suggestion?.label || suggestion?.address || suggestion?.fullAddress || "";
    if (!value) return;
    const parsed = splitInvoiceAddressLine(value);
    setInvoiceAddress((current) => ({
      companyName: current.companyName,
      street: parsed.street || value,
      postalCode: parsed.postalCode,
      city: parsed.city,
    }));
    setInvoiceSuggestions([]);
  };

  const renderInvoiceSuggestions = (suggestions, onPick) =>
    suggestions.length > 0 ? (
      <div className="address-suggestions">
        {suggestions.map((suggestion, idx) => {
          const label =
            typeof suggestion === "string"
              ? suggestion
              : suggestion?.label ||
                suggestion?.address ||
                suggestion?.fullAddress ||
                "";
          if (!label) return null;
          return (
            <button
              type="button"
              key={`${label}-${idx}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(suggestion)}
            >
              {label}
            </button>
          );
        })}
      </div>
    ) : null;

  const renderBusinessInvoiceAddress = () => {
    if (!requiresBusinessInvoice) return null;
    return (
      <>
        <div className="full">
          <h2>
            <em>Rechnungsadresse</em>
          </h2>
        </div>
        <div className="full">
          <label htmlFor="invoice-company">Unternehmensname *</label>
          <input
            id="invoice-company"
            {...invalidFieldProps("invoice-company")}
            value={invoiceAddress.companyName}
            onChange={updateInvoiceAddress("companyName")}
            autoComplete="organization"
          />
        </div>
        <div className="full">
          <label htmlFor="invoice-street">Straße &amp; Hausnummer *</label>
          <input
            id="invoice-street"
            {...invalidFieldProps("invoice-street")}
            value={invoiceAddress.street}
            onChange={updateInvoiceAddress("street")}
            placeholder="Straße Hausnummer"
            autoComplete="street-address"
          />
          {renderInvoiceSuggestions(invoiceSuggestions, applyInvoiceSuggestion)}
        </div>
        <div>
          <label htmlFor="invoice-plz">PLZ *</label>
          <input
            id="invoice-plz"
            {...invalidFieldProps("invoice-plz")}
            value={invoiceAddress.postalCode}
            onChange={updateInvoiceAddress("postalCode")}
            autoComplete="postal-code"
            maxLength={10}
          />
        </div>
        <div>
          <label htmlFor="invoice-city">Ort *</label>
          <input
            id="invoice-city"
            {...invalidFieldProps("invoice-city")}
            value={invoiceAddress.city}
            onChange={updateInvoiceAddress("city")}
            autoComplete="address-level2"
          />
        </div>
      </>
    );
  };

  const applyAddressSuggestion = (suggestion) => {
    const value =
      typeof suggestion === "string"
        ? suggestion
        : suggestion?.label || suggestion?.address || suggestion?.fullAddress || "";
    if (!value) return;
    setProfileAddressPrefilled(false);
    setAddress((prev) => ({ ...prev, fullAddress: value }));
    setAddressSuggestions([]);
    setServiceAreaCheck({ status: "idle", message: "" });
    lastCheckedAddressRef.current = "";
    if (requiresServiceAreaCheck) {
      checkServiceArea(value).then((result) => {
        if (result !== null) lastCheckedAddressRef.current = value.trim();
      });
    }
  };

  const checkServiceArea = useCallback(
    async (addressOverride) => {
    if (!selectedOption || !requiresServiceAreaCheck) return null;
    const addressToCheck = String(addressOverride ?? selectedFullAddress).trim();
    if (!addressToCheck) {
      setServiceAreaCheck({
        status: "needs-address",
        message: "Bitte gib zuerst eine Massageadresse ein.",
      });
      return null;
    }
    setServiceAreaCheck({
      status: "checking",
      message: "Massageadresse wird geprüft ...",
    });
    try {
      const { data } = await api.post("/service-area/check", {
        brand: PRICE_PREVIEW_BRAND,
        serviceKey: selectedOption.serviceKey,
        offerType: selectedOption.offerType,
        address: addressToCheck,
      });
      if (data?.eligible) {
        const next = {
          status: "eligible",
          message: "Massageadresse liegt im Hamburger Stadtgebiet. Du kannst jetzt buchen.",
          serviceAreaEligible: true,
          serviceAreaVersion: data.serviceAreaVersion || data.areaVersion,
          serviceAreaCheckStatus: "passed",
          serviceAreaReason: data.reason || "",
          geocodedAddress: data.normalizedAddress || data.geocodedAddress || data.address,
        };
        setServiceAreaCheck(next);
        return next;
      }
      const next = {
        status:
          data?.status === "address-not-found" ||
          data?.status === "geocoding-unavailable"
            ? "unknown"
            : "outside",
        message:
          data?.status === "address-not-found" ||
          data?.status === "geocoding-unavailable"
            ? "Wir konnten die Massageadresse nicht sicher prüfen."
            : "Diese Massageadresse liegt außerhalb des Hamburger Stadtgebiets.",
        serviceAreaEligible: false,
        serviceAreaVersion: data?.serviceAreaVersion || data?.areaVersion,
        serviceAreaCheckStatus: data?.status || "not-eligible",
        serviceAreaReason: data?.reason || "",
      };
      setServiceAreaCheck(next);
      return next;
    } catch {
      const next = {
        status: "unknown",
        message: "Wir konnten die Massageadresse nicht sicher prüfen.",
        serviceAreaEligible: false,
        serviceAreaCheckStatus: "geocoding-unavailable",
      };
      setServiceAreaCheck(next);
      return next;
    }
  },
    [requiresServiceAreaCheck, selectedFullAddress, selectedOption]
  );

  useEffect(() => {
    if (!profileAddressPrefilled || !requiresServiceAreaCheck) return undefined;
    const trimmed = selectedFullAddress.trim();
    if (!trimmed || trimmed === lastCheckedAddressRef.current) return undefined;
    if (serviceAreaCheck.status !== "idle") return undefined;

    const timer = window.setTimeout(() => {
      checkServiceArea(trimmed).then((result) => {
        if (result !== null) lastCheckedAddressRef.current = trimmed;
      });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [
    checkServiceArea,
    profileAddressPrefilled,
    requiresServiceAreaCheck,
    selectedFullAddress,
    serviceAreaCheck.status,
  ]);

  const handleFullAddressBlur = useCallback((event) => {
    if (!requiresServiceAreaCheck) return;
    const shouldScroll = !event.relatedTarget?.closest?.(".person-counter");
    const trimmed = selectedFullAddress.trim();
    if (!trimmed) {
      lastCheckedAddressRef.current = "";
      return;
    }
    if (trimmed === lastCheckedAddressRef.current) return;
    checkServiceArea().then((result) => {
      if (result !== null) lastCheckedAddressRef.current = trimmed;
      if (shouldScroll && result?.serviceAreaEligible) {
        window.requestAnimationFrame(() =>
          scrollToNextBookingStep(
            optionDetailRef.current?.querySelector("#ad-full-address")
          )
        );
      }
    });
  }, [requiresServiceAreaCheck, selectedFullAddress, checkServiceArea, scrollToNextBookingStep]);

  const submitAuthedBooking = async () => {
    setError(null);
    setErrorAction(null);
    setValidationAttempted(true);
    if (businessEventBookingClosed) {
      return setError(
        "Die Buchung ist nur bis Sonntag, 14.06.2026 um 16:00 Uhr möglich."
      );
    }
    if (missingFields.length > 0 || loadingSlots) return;
    if (!selectedSlot && !appointmentIsArranged) {
      return setError("Bitte wähle einen Termin aus.");
    }
    if (
      isLicensePartnerOption(selectedOption) &&
      startOfDay(selectedDate || new Date(selectedSlot)) < selectedMinDate
    ) {
      return setError(
        "Lizenzpartner-Termine sind erst zwei Werktage sp\u00e4ter buchbar, damit wir uns vorbereiten k\u00f6nnen."
      );
    }
    if (voucherChoiceMissing) {
      return setError("Bitte wähle aus, ob du einen Gutschein hast.");
    }
    if (durationChoiceMissing) {
      return setError("Bitte wähle eine Dauer aus.");
    }
    if (hasVoucher === true && !voucherTermsAccepted) {
      return setError("Bitte bestätige, dass du den Gutschein zum Termin mitbringst.");
    }
    if (!bookingConsents.legalTerms) {
      return setError(
        "Bitte bestätige AGB, Datenschutzhinweise und Widerrufsbelehrung vor der verbindlichen Buchung."
      );
    }
    if (requiresOnsiteBusinessIdentity) {
      if (!String(address.companyName || "").trim()) {
        return setError("Bitte gib Name oder Firma für den Vor-Ort-Termin an.");
      }
      if (!selectedFullAddress) {
        return setError("Bitte gib die Massageadresse an.");
      }
    }
    if (requiresBusinessInvoice) {
      if (
        !String(invoiceAddress.companyName || "").trim() ||
        !String(invoiceAddress.street || "").trim() ||
        !String(invoiceAddress.postalCode || "").trim() ||
        !String(invoiceAddress.city || "").trim()
      ) {
        return setError(
          "Bitte gib den Unternehmensnamen und die Rechnungsadresse mit Straße, Hausnummer, PLZ und Ort an."
        );
      }
    }
    if (selectedOption.bookingMode === "mobile") {
      if (selectedOption.usesLegacyAddressFields) {
        if (!address.street || !address.postalCode || !address.city) {
          return setError("Bitte gib die vollständige Massageadresse an.");
        }
      } else if (!selectedFullAddress) {
        return setError("Bitte gib die vollständige Massageadresse an.");
      }
      if (requiresServiceAreaCheck && !addressIsEligible) {
        return setError("Bitte prüfe zuerst deine Massageadresse.");
      }
      if (!selectedOption.skipPostalCheck && !plzIsValid) {
        return setError(
          "Diese Postleitzahl liegt außerhalb des Hamburger Stadtgebiets."
        );
      }
    }
    if (!user || !token) {
      persistDraft();
      return setError(
        "Bitte melde dich an oder registriere dich kostenlos, um die Buchung abzuschließen."
      );
    }
    setSubmitting(true);
    try {
      const isConsultation = selectedOption.bookingMode === "consultation";
      const isWakeup = isWakeupBookingOption(selectedOption);
      const serviceDuration = wakeupServiceDurationMinutes(
        selectedOption,
        selectedPersonCount
      );
      const body = {
        serviceKey: selectedOption.serviceKey,
        paymentMethod:
          isConsultation &&
          !selectedOption.paidPackage &&
          !isLicensePartnerOnsiteOption(selectedOption)
            ? "later"
            : requiresStripeOnlyPayment(selectedOption)
              ? "stripe"
              : effectivePaymentMethod,
      };
      body.durationMinutes = isWakeupPlusOption(selectedOption)
        ? selectedOption.durationMinutes
        : isWakeupBookingOption(selectedOption)
          ? serviceDuration
          : selectedDurationMinutes;
      if (isWakeup) {
        body.serviceDurationMinutes = serviceDuration;
      }
      if (selectedOption.offerType) {
        body.offerType = selectedOption.offerType;
      }
      if (isWakeupPlusOption(selectedOption) && monthlyActionVariant) {
        body.monthlyActionVariant = monthlyActionVariant;
        body.monthlyActionPeriod = monthlyAction.period;
      }
      if (isLicensePartnerOption(selectedOption)) {
        body.licensePartnerLocation = selectedOption.licensePartnerLocation;
      }
      if (selectedOption.minimumLeadMinutes) {
        body.minimumLeadMinutes = selectedOption.minimumLeadMinutes;
      }
      if (selectedOption.personCountDefault || selectedOption.personCountMax) {
        body.personCount = selectedPersonCount;
      }
      if (selectedOption.requiresVoucherTerms) {
        body.voucherTermsAccepted = Boolean(voucherTermsAccepted);
      }
      if (selectedOption.requiresVoucherQuestion) {
        body.hasVoucher = Boolean(hasVoucher);
        body.voucherTermsAccepted = Boolean(hasVoucher && voucherTermsAccepted);
        body.requiresVoucherPresentation = Boolean(hasVoucher);
      }
      if (selectedSlot) {
        body.startISO = selectedSlot;
      } else if (isBusinessEventOption(selectedOption)) {
        body.startISO = HOFFMANN_BUSINESS_EVENT_START_ISO;
      } else if (appointmentIsArranged) {
        body.startISO = arrangedAppointmentStartISO();
      }
      if (needsBookingPhone && bookingPhone.trim()) {
        body.phone = bookingPhone.trim();
      }
      if (isBusinessEventOption(selectedOption)) {
        body.priceCents = paidBasePriceCents;
        body.location = selectedOption.eventLocation || HOFFMANN_BUSINESS_EVENT_LOCATION;
        body.hoffmannBookingOption =
          selectedOption.hoffmannBookingOption || selectedOption.title;
      } else if (selectedOption.paidPackage && selectedOption.priceCents) {
        body.priceCents = selectedOption.priceCents;
      }
      if (
        isFixedGrossIndividualOption(selectedOption) &&
        currentPricePreview?.grossCents
      ) {
        body.priceCents = currentPricePreview.grossCents;
      }
      if (voucherActive && selectedOption.voucherType) {
        body.voucherType = selectedOption.voucherType;
        body.applyFirstBookingVoucher = true;
      }
      if (isConsultation) {
        const notes = [
          appointmentIsArranged
            ? "Termin und Uhrzeit werden telefonisch abgestimmt."
            : "",
          consultationNotes.trim(),
        ]
          .filter(Boolean)
          .join("\n");
        if (notes) body.notes = notes;
      }
      if (isConsultation) {
        body.hoffmannBookingOption =
          selectedOption.hoffmannBookingOption || selectedOption.title;
        if (!isLicensePartnerOnsiteOption(selectedOption)) {
          body.location = selectedStudioLocation;
        }
      }
      if (isStudioLikeBooking(selectedOption.bookingMode)) {
        const studioSchedule = studioScheduleForOption(
          selectedOption,
          selectedSlot || selectedDate
        );
        body.location = studioLocationForOption(
          selectedOption,
          selectedSlot || selectedDate
        );
        body.studioSchedule = studioSchedule;
        body.studioScheduleWeekday =
          selectedOption.bookingMode === "studio-weekday" &&
          (!usesDailyHoffmannSchedule(selectedOption) ||
            studioSchedule === "thursday");
        body.hoffmannBookingOption = selectedOption.title;
      }
      if (selectedOption.bookingMode === "mobile") {
        body.serviceAddress = buildServiceAddressForOption(selectedOption, {
          ...address,
          geocodedAddress: requiresServiceAreaCheck
            ? serviceAreaCheck.geocodedAddress
            : "",
        });
        if (selectedFullAddress) {
          body.address = selectedFullAddress;
        }
        body.hoffmannBookingOption = selectedOption.title;
        if (requiresServiceAreaCheck) {
          body.serviceAreaEligible = serviceAreaCheck.serviceAreaEligible;
          body.serviceAreaVersion = serviceAreaCheck.serviceAreaVersion;
          body.serviceAreaCheckStatus =
            serviceAreaCheck.serviceAreaEligible === true
              ? "passed"
              : serviceAreaCheck.serviceAreaCheckStatus;
          body.serviceAreaReason = serviceAreaCheck.serviceAreaReason;
          body.geocodedAddress = serviceAreaCheck.geocodedAddress;
        }
      }
      if (requiresBusinessInvoice) {
        body.invoiceAddress = {
          companyName: String(invoiceAddress.companyName || "").trim(),
          street: String(invoiceAddress.street || "").trim(),
          postalCode: String(invoiceAddress.postalCode || "").trim(),
          city: String(invoiceAddress.city || "").trim(),
        };
      }
      if (canUseReferralBalance) {
        body.applyReferralBalance = applyReferralBalance;
      }
      if (selectedMassageVoucher) {
        body.massageVoucherId = selectedMassageVoucher.id;
        body.paymentMethod = "voucher";
        body.priceCents = 0;
        body.hasVoucher = false;
        body.applyReferralBalance = false;
      }
      body.consents = {
        termsAccepted: bookingConsents.legalTerms,
        privacyNoticeRead: bookingConsents.legalTerms,
        withdrawalInstructionAccepted: bookingConsents.legalTerms,
        capturedAt: new Date().toISOString(),
        source: "dr-hoffmann-booking",
      };
      if (process.env.NODE_ENV === "development") {
        console.debug("Booking payload", {
          serviceKey: selectedOption.serviceKey,
          offerType: selectedOption.offerType,
          personCount: selectedPersonCount,
          hasStartISO: Boolean(body.startISO),
          hasPaymentMethod: Boolean(body.paymentMethod),
          hasServiceAddress: Boolean(body.serviceAddress),
          hasAddress: Boolean(body.address),
          areaCheckEligible:
            !requiresServiceAreaCheck || addressIsEligible,
          durationMinutes: body.durationMinutes,
          serviceDurationMinutes: body.serviceDurationMinutes,
        });
      }
      let data;
      try {
        ({ data } = await api.post("/bookings", body));
      } catch (bookingErr) {
        const responseMessage = String(
          bookingErr?.response?.data?.message || bookingErr?.message || ""
        );
        if (/missing_service_address/i.test(responseMessage)) {
          throw new Error(
            "Die geprüfte Massageadresse konnte nicht übernommen werden. Bitte prüfe deine Massageadresse erneut."
          );
        }
        if (/10:00|22:00|zwischen 10:00 und 22:00/i.test(responseMessage)) {
          throw new Error(
            "Bitte w\u00e4hle eine Startzeit zwischen 10:00 und 22:00 Uhr."
          );
        }
        if (/12:00|18:00|19:00|19:30|20:00|startzeit/i.test(responseMessage)) {
          throw new Error(
            usesIndividualSchedule(selectedOption)
              ? "Bitte w\u00e4hle eine Startzeit zwischen 10:00 und 22:00 Uhr."
              : "Bitte w\u00e4hle eine Startzeit zwischen 12:00 und 19:30 Uhr."
          );
        }
        if (/missing_required.*field|pflichtfeld/i.test(responseMessage)) {
          const missingFields = bookingErr?.response?.data?.missingFields || [];
          const fieldMap = {
            startISO: "Termin",
            paymentMethod: "Zahlungsart",
            serviceAddress: "Massageadresse",
            address: "Massageadresse",
            invoiceAddress: "Rechnungsadresse",
            companyName: "Unternehmensname",
            personCount: "Personenanzahl",
            serviceKey: "Angebot",
            offerType: "Angebotstyp",
          };
          const readableFields = missingFields
            .map((f) => fieldMap[f] || f)
            .join(", ");
          throw new Error(
            readableFields
              ? `Bitte prüfe deine Angaben. Es fehlen noch: ${readableFields}.`
              : "Bitte prüfe deine Angaben. Es fehlen noch erforderliche Felder."
          );
        }
        if (
          selectedOption.legacyServiceKey &&
          /leistung|service|angebot/i.test(responseMessage)
        ) {
          ({ data } = await api.post("/bookings", {
            ...body,
            serviceKey: selectedOption.legacyServiceKey,
          }));
        } else {
          throw bookingErr;
        }
      }
      const booking = data.booking;
      // Trust server price after Guthaben: fully covered bookings are confirmed
      // with priceCents 0 and must not open Stripe checkout.
      const needsStripeCheckout =
        booking?.status === "pending-payment" &&
        booking?.paymentMethod === "stripe" &&
        Number(booking?.priceCents) > 0;

      if (needsStripeCheckout) {
        try {
          const checkoutPriceCents = isBusinessEventOption(selectedOption)
            ? paidBasePriceCents
            : selectedOption.paidPackage && selectedOption.priceCents
              ? selectedOption.priceCents
              : isFixedGrossIndividualOption(selectedOption) &&
                  currentPricePreview?.grossCents
                ? currentPricePreview.grossCents
                : undefined;
          const { data: checkout } = await api.post(
            `/payment/checkout/${booking._id}`,
            checkoutPriceCents
              ? {
                  priceCents: checkoutPriceCents,
                  serviceLabel: selectedOption.title,
                  hoffmannBookingOption: selectedOption.hoffmannBookingOption,
                }
              : undefined
          );
          if (!checkout?.url) {
            throw new Error("Zahlungsseite konnte nicht geöffnet werden.");
          }
          clearBookingDraft();
          window.location.href = checkout.url;
          return;
        } catch (checkoutErr) {
          try {
            await api.delete(`/bookings/${booking._id}`);
          } catch {
            /* slot cleanup best-effort */
          }
          throw new Error(
            checkoutErr?.response?.data?.message ||
              checkoutErr?.message ||
              "Die Online-Zahlung f\u00fcr das Starterpaket konnte nicht gestartet werden. Die Buchung wurde nicht angelegt."
          );
        }
      }
      try {
        const { data: me } = await api.get("/auth/me");
        if (me?.user) dispatch(setUser({ user: me.user }));
      } catch {
        /* ignore */
      }
      clearBookingDraft();
      navigate(`/buchen/erfolg?bookingId=${booking._id}`, {
        state: { booking },
      });
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "";
      if (err?.response?.status === 401) {
        dispatch(setLogout());
        persistDraft();
        const params = new URLSearchParams({
          next: `${location.pathname}${location.search}`,
        });
        navigate(`/einloggen?${params.toString()}`);
        return;
      }
      if (err?.response?.data?.requiresSpecialRequest) {
        const offer =
          selectedOption?.offerType === "wakeup_plus_business"
            ? "wakeup-plus"
            : "wakeup-onsite";
        setError(
          message ||
            "Diese Buchung braucht eine Sonderanfrage. Bitte nutze unser Kontaktformular."
        );
        setErrorAction({
          href: `/kontakt?reason=outside-area&offer=${offer}&address=${encodeURIComponent(selectedFullAddress)}`,
          label: "Sonderanfrage stellen",
        });
      } else if (
        selectedOption?.offerType &&
        /leistung|service|angebot/i.test(message)
      ) {
        setError(
          "Dieses Angebot ist gerade noch nicht verfügbar. Bitte stelle eine Anfrage über unser Kontaktformular."
        );
        setErrorAction({
          href: `/kontakt?reason=service-unavailable&offer=${selectedOption.id}&personCount=${selectedPersonCount}`,
          label: "Zum Kontaktformular",
        });
      } else {
        setError(message || "Die Buchung konnte nicht angelegt werden.");
      }
    } finally {
      setSubmitting(false);
    }
    return undefined;
  };

  return (
    <Layout>
      <section className="booking-page">
        <h1 className="booking-title">
          Jetzt <em>Termin</em> buchen
        </h1>
        <p className="booking-intro">
          Wähle die passende Option und fülle die Buchung in Ruhe aus.
          Anmeldung oder Registrierung erfolgt erst beim Abschließen.
        </p>
        <div className="booking-audience-switch" role="tablist" aria-label="Buchungsart">
          {BOOKING_AUDIENCES.map((audience) => {
            const isActive = bookingAudience === audience.id;
            return (
              <button
                key={audience.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`booking-audience-tab ${isActive ? "is-active" : ""}`}
                onClick={() => handleAudienceChange(audience.id)}
              >
                {audience.label}
              </button>
            );
          })}
        </div>
        <div className="booking-audience-copy">
          <strong>{activeAudience.title}</strong>
          <span>{activeAudience.description}</span>
          <p>
            Du möchtest uns{" "}
            <Link to="/empfehlungsprogramm">weiterempfehlen</Link>
            {" "}und 5 € Massage-Guthaben erhalten?
          </p>
        </div>
        <div
          className={`option-grid option-grid--booking ${
            bookingAudience === AUDIENCE_INDIVIDUAL
              ? "option-grid--individual"
              : "option-grid--business"
          }`}
        >
          {visibleOptions.map((option) => {
            const isSelected = selectedOption?.id === option.id;
            return (
              <div
                key={option.id}
                className={`option-card option-card--${option.id} ${isSelected ? "is-selected" : ""}`}
              >
                {isWakeupPlusOption(option) ? (
                  <div className="option-badge-row">
                    <button
                      type="button"
                      className="option-featured-badge option-featured-badge--action"
                      onClick={() => handleSelectOption(option, { monthlyAction: true })}
                      aria-label={`${monthlyAction.label} buchen`}
                    >
                      {monthlyAction.label}
                    </button>
                    <span className="option-featured-badge">{option.spotlight}</span>
                  </div>
                ) : option.spotlight && (
                  <div className="option-featured-badge">
                    {option.spotlight}
                  </div>
                )}
                <div className="option-card-content">
                  <h3>{option.title}</h3>
                  {option.subtitle && (
                    <p className="option-subtitle">{option.subtitle}</p>
                  )}
                  {!option.detailsId && (
                    <p>
                      {option.highlight && (
                        <strong className="option-highlight-red">
                          {option.highlight}
                        </strong>
                      )}
                      {option.highlight ? " " : ""}
                      {option.description}
                    </p>
                  )}
                  {option.features && (
                    <ul className="option-feature-list">
                      {option.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  )}
                  <div className="option-actions">
                    {option.detailsId && (
                      <button
                        type="button"
                        className="offer-details-trigger"
                        onClick={() => setOpenOfferDetails(option.detailsId)}
                      >
                        Mehr erfahren
                      </button>
                    )}
                    {option.externalUrl ? (
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={() => handleSelectOption(option)}
                      >
                        {option.externalCta || "Zur Buchung →"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={isSelected ? "btn-outline" : "btn-primary"}
                        onClick={() => handleSelectOption(option)}
                      >
                        {isSelected ? "Auswahl aktiv" : option.cta || "Termin auswählen"}
                      </button>
                    )}
                  </div>
                </div>
                {option.badge && <div className="option-badge">{option.badge}</div>}
                {option.hint && <p className="option-hint">{option.hint}</p>}
                <OptionPriceDisplay option={option} user={user} />
              </div>
            );
          })}
        </div>
        
          <div className="booking-special-request">
            <span>Sonderwünsche, Events oder größere Gruppen?</span>
            <Link to={SPECIAL_REQUEST_OPTION.specialRequestPath} className="btn-outline">
              {SPECIAL_REQUEST_OPTION.cta}
            </Link>
          </div>
        
        {selectedOption && (
          <div
            className="option-detail"
            ref={optionDetailRef}
            onChangeCapture={handleBookingFieldChangeCapture}
            onBlurCapture={handleBookingFieldBlurCapture}
          >
            <div className="booking-step">
              <div className="step-head">
                <h2>
                  {selectedOption.title} — <em>Datum &amp; Uhrzeit</em>
                </h2>
                <span className="booking-step-number">01</span>
              </div>
              {isBusinessEventOption(selectedOption) && (
                <div className="booking-summary-block" style={{ marginTop: "1rem", borderTop: "none", paddingTop: 0 }}>
                  <div className="summary-row">
                    <span>Datum &amp; Uhrzeit</span>
                    <strong>{HOFFMANN_BUSINESS_EVENT_SCHEDULE_LABEL}</strong>
                  </div>
                  <div className="summary-row">
                    <span>Ort</span>
                    <strong>
                      {selectedOption.eventLocation || HOFFMANN_BUSINESS_EVENT_LOCATION}
                    </strong>
                  </div>
                  {showPersonCount && (
                    <div className="address-grid" style={{ marginTop: "1rem" }}>
                      <div className="full">
                        <label htmlFor="person-count-event">{personCountLabel} *</label>
                        {renderPersonCountInput("person-count-event")}
                        <p className="field-note">
                          Maximal {HOFFMANN_BUSINESS_EVENT_MAX_SEATS_PER_USER} Plätze pro Person.
                        </p>
                      </div>
                      {needsBookingPhone && (
                        <div className="full">
                          <label htmlFor="booking-phone-event">Telefonnummer (optional)</label>
                          <input
                            id="booking-phone-event"
                            type="tel"
                            autoComplete="tel"
                            placeholder="+49 ..."
                            value={bookingPhone}
                            onChange={(e) => setBookingPhone(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isDeliveryChoiceOption(selectedOption) && !isBusinessPlusOption(selectedOption) && (
                <p className="booking-gift-prompt">
                  Möchtest du diese Massage einem Freund{" "}
                  <Link
                    to={`/shop?${new URLSearchParams({
                      massage: selectedOption.deliveryGroup === "premium" ? "intense" : selectedOption.deliveryGroup,
                      gift: "1",
                      delivery: wakeupComesToYou ? "onsite" : "studio",
                      ...(selectedOption.deliveryGroup === "wakeup"
                        ? { persons: String(selectedPersonCount) }
                        : {}),
                      ...(selectedOption.deliveryGroup === "premium" && selectedDurationMinutes
                        ? { duration: String(selectedDurationMinutes) }
                        : {}),
                    }).toString()}`}
                  >
                    schenken
                  </Link>
                  ?
                </p>
              )}
              {isDeliveryChoiceOption(selectedOption) && (
                <div className="booking-delivery-choice" aria-label={`${selectedOption.title} Ort`}>
                  <strong>
                    {isLicensePartnerOption(selectedOption)
                      ? "Sollen wir zu dir kommen?"
                      : "Sollen wir zu dir kommen?"}
                  </strong>
                  <div className="booking-delivery-options">
                    <label className={!wakeupComesToYou ? "is-selected" : ""}>
                      <input
                        type="radio"
                        name="wakeup-delivery"
                        checked={!wakeupComesToYou}
                        onChange={() => handleWakeupDeliveryChange(false)}
                      />
                      {isLicensePartnerOption(selectedOption)
                        ? "Nein, im Studio"
                        : "Nein, im Studio"}
                    </label>
                    <label className={wakeupComesToYou ? "is-selected" : ""}>
                      <input
                        type="radio"
                        name="wakeup-delivery"
                        checked={wakeupComesToYou}
                        onChange={() => handleWakeupDeliveryChange(true)}
                      />
                      <span>
                        {isLicensePartnerOption(selectedOption)
                          ? "Ja, bitte vor Ort"
                          : "Ja, bitte vor Ort"}
                      </span>
                      <span className="booking-delivery-badge">
                        {isBusinessPlusOption(selectedOption)
                          ? "+ 39 € Anfahrt netto"
                          : "+ 39 € Anfahrt"}
                      </span>
                    </label>
                  </div>
                  {isLicensePartnerOption(selectedOption) && (
                    <p className="field-note">
                      Im Cremon 11 ist der Erstbesuch kostenfrei. Bei einer Wunschadresse
                      im Hamburger Stadtgebiet berechnen wir 39 € Anfahrtspauschale.<br></br>
                      Der Gutschein ist für den Lizenzpartner-Besuch <b>nicht</b> gültig.
                    </p>
                  )}
                  {selectedOption.requiresVoucherQuestion && !selectedMassageVoucher && (
                    <div className={`voucher-question booking-delivery-voucher ${validationAttempted && missingFieldIds.has("has-voucher") ? "booking-field-invalid" : ""}`}>
                      <span className="voucher-question__label">
                        Hast du einen Anfahrtsgutschein? *
                      </span>
                      <div className="voucher-question__choices">
                        <label>
                          <input
                          type="radio"
                          name="has-voucher"
                          value="yes"
                          checked={hasVoucher === true}
                            onChange={() => {
                              setHasVoucher(true);
                              setVoucherTermsAccepted(false);
                            }}
                          />
                          Ja
                        </label>
                        <label>
                          <input
                          type="radio"
                          name="has-voucher"
                          value="no"
                          checked={hasVoucher === false}
                            onChange={() => {
                              setHasVoucher(false);
                              setVoucherTermsAccepted(false);
                            }}
                          />
                          Nein
                        </label>
                      </div>
                      {hasVoucher === true && (
                        <>
                          <div className="booking-notice">
                            <strong>Gutschein bitte beim Termin vorzeigen.</strong>
                            <p>
                              Der Gutschein spart die Anfahrtskosten und ist nicht
                              kombinierbar.
                            </p>
                          </div>
                          <label className={`booking-checkbox ${validationAttempted && missingFieldIds.has("voucher-terms") ? "booking-field-invalid" : ""}`}>
                            <input
                              type="checkbox"
                              checked={voucherTermsAccepted}
                              onChange={(e) =>
                                setVoucherTermsAccepted(e.target.checked)
                              }
                            />
                            <span>Ich bringe den Gutschein zum Termin mit.</span>
                          </label>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isWakeupPlusOption(selectedOption) && (
                <div className="booking-monthly-action">
                  <label className={`booking-checkbox ${monthlyActionUnavailable ? "is-disabled" : ""}`}>
                    <input
                      type="checkbox"
                      checked={Boolean(monthlyActionVariant)}
                      disabled={monthlyActionUnavailable}
                      onChange={(event) => {
                        const enabled = event.target.checked;
                        setMonthlyActionVariant(enabled ? "wakeup" : "");
                        setPersonCount(defaultPersonCountForOption(selectedOption));
                        setSelectedSlot(null);
                        const nextParams = new URLSearchParams(location.search);
                        if (enabled) nextParams.set("monthlyAction", "1");
                        else nextParams.delete("monthlyAction");
                        navigate(`/buchen?${nextParams.toString()}`, { replace: true });
                      }}
                    />
                    <span>{monthlyAction.label} hinzufügen</span>
                  </label>
                  {monthlyActionUsed && (
                    <p className="field-note">Du hast die Monatsaktion bereits gebucht.</p>
                  )}
                  {token && monthlyActionUsed === null && (
                    <p className="field-note">Wir prüfen, ob du die Aktion buchen kannst…</p>
                  )}
                  {monthlyActionVariant ? (
                    <fieldset className="booking-monthly-action-choices">
                      <legend>Wähle eine Variante</legend>
                      {MONTHLY_ACTION_VARIANTS.map((variant) => (
                        <label key={variant.id}>
                          <input
                            type="radio"
                            name="monthly-action-variant"
                            value={variant.id}
                            checked={monthlyActionVariant === variant.id}
                            onChange={() => {
                              setMonthlyActionVariant(variant.id);
                              setPersonCount(variant.personCount);
                              setSelectedSlot(null);
                            }}
                          />
                          <span>{variant.label}</span>
                        </label>
                      ))}
                      <p>79 € netto zzgl. MwSt. · Maximal 30 Minuten · Nur für Neukunden.</p>
                    </fieldset>
                  ) : (
                    <div className="address-grid booking-count-choice">
                      <div className="full">
                        <label htmlFor="person-count-mobile">{personCountLabel} *</label>
                        {renderPersonCountInput("person-count-mobile")}
                        <p className="field-note">
                          1-10 Mitarbeitende: 79 € netto zzgl. MwSt. Jede weitere Person: +5 € netto.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {showDurationChoice && (
                <div className="address-grid">
                  <div className="full">
                    <label htmlFor="duration-minutes">
                      {isIntensePlusBusinessOption(selectedOption)
                        ? "Dauer (Stunden) *"
                        : "Dauer *"}
                    </label>
                    <select
                      id="duration-minutes"
                      {...invalidFieldProps("duration-minutes")}
                      value={selectedDurationMinutes ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDurationMinutes(value ? Number(value) : "");
                      }}
                    >
                      <option value="" disabled>
                        Bitte wählen…
                      </option>
                      {durationChoices.map((minutes) => (
                        <option key={minutes} value={minutes}>
                          {isIntensePlusBusinessOption(selectedOption)
                            ? `${minutes / 60} ${minutes === 60 ? "Stunde" : "Stunden"}`
                            : `${minutes} Min.`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {appointmentIsArranged && !isBusinessEventOption(selectedOption) ? (
                <div className="booking-summary-block" style={{ marginTop: "1rem", borderTop: "none", paddingTop: 0 }}>
                  <div className="summary-row">
                    <span>Datum</span>
                    <strong>Nach Vereinbarung</strong>
                  </div>
                  <div className="summary-row">
                    <span>Uhrzeit</span>
                    <strong>Nach Vereinbarung</strong>
                  </div>
                  <p className="step-summary" style={{ marginTop: "1rem" }}>
                    Wir stimmen den Termin für das Starterpaket telefonisch mit dir ab.
                  </p>
                </div>
              ) : !isBusinessEventOption(selectedOption) ? (
                <div className={`booking-calendar-target ${validationAttempted && missingFieldIds.has("booking-date") ? "booking-field-invalid" : ""}`}>
                  <Calendar
                    selectedDate={selectedDate}
                    onSelect={(date) => {
                      scrollToSlotsAfterDateSelectionRef.current = true;
                      setSelectedDate(date);
                      setSelectedSlot(null);
                      setSlots([]);
                    }}
                    minDate={selectedMinDate}
                    maxDate={selectedMaxDate}
                    weekdaysOnly={
                      !isBusinessEventOption(selectedOption) &&
                      !isThursdayOnlyStudio(selectedOption) &&
                      !usesDailyHoffmannSchedule(selectedOption)
                    }
                    onlyWeekday={
                      isThursdayOnlyStudio(selectedOption) ? 4 : -1
                    }
                    closedWeekday={-1}
                    closedDayTitle={
                      isThursdayOnlyStudio(selectedOption)
                        ? "Studio-Massagen nur Donnerstags"
                        : undefined
                    }
                    disableDate={
                      isThursdayOnlyStudio(selectedOption)
                        ? isAfterHoffmannOption1Cutoff
                        : undefined
                    }
                    disableDateTitle="Studio Massagen sind nur bis Ende Juni buchbar"
                  />
                </div>
              ) : null}
              {isLicensePartnerOption(selectedOption) && (
                <p className="step-summary" style={{ marginTop: "1rem" }}>
                  {"Lizenzpartner-Termine sind frühestens zwei Werktage im Voraus buchbar, damit wir den Erstbesuch vorbereiten können."}
                </p>
              )}
              {selectedDate && !isBusinessEventOption(selectedOption) && (
                <>
                  {bookingHoursHintForOption(selectedOption, selectedDate) && (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {bookingHoursHintForOption(selectedOption, selectedDate)}
                    </p>
                  )}
                  {slots.length > 0 ? (
                    <div
                      className={`slot-grid ${validationAttempted && missingFieldIds.has("booking-slot") ? "booking-field-invalid" : ""} ${loadingSlots ? "is-loading" : ""}`}
                      aria-busy={loadingSlots}
                    >
                      {slots.map((iso) => (
                        <button
                          type="button"
                          key={iso}
                          disabled={loadingSlots}
                          onClick={(event) => {
                            setSelectedSlot(iso);
                            scrollToNextBookingStep(event.currentTarget);
                          }}
                          className={`slot-button ${selectedSlot === iso ? "is-selected" : ""}`}
                        >
                          {formatTime(iso)}
                        </button>
                      ))}
                      {loadingSlots && (
                        <div className="slot-grid-loading" role="status">
                          Zeiten werden aktualisiert…
                        </div>
                      )}
                    </div>
                  ) : loadingSlots ? (
                    <p className="step-summary" style={{ marginTop: "1rem" }} role="status">
                      Verfügbare Zeiten werden geladen…
                    </p>
                  ) : (
                    <p className="step-summary" style={{ marginTop: "1rem" }}>
                      {availabilityError || "An diesem Tag sind keine passenden Termine mehr verfügbar. Bitte wähle einen späteren Zeitpunkt."}
                    </p>
                  )}
                </>
              )}
            </div>

            {selectedOption.bookingMode === "consultation" && (
              <div className="booking-step">
                <div className="step-head">
                  <h2>
                    Deine <em>Hinweise</em>
                  </h2>
                  <span className="booking-step-number">02</span>
                </div>
                <p className="step-summary">
                  Die Bestätigung geht an die E-Mail-Adresse deines Kontos. Optional kannst du uns hier noch etwas mitteilen.
                </p>
                <div className="address-grid">
                  {needsBookingPhone && (
                    <div className="full">
                      <label htmlFor="booking-phone-consultation">Telefonnummer (optional)</label>
                      <input
                        id="booking-phone-consultation"
                        type="tel"
                        autoComplete="tel"
                        placeholder="+49 ..."
                        value={bookingPhone}
                        onChange={(e) => setBookingPhone(e.target.value)}
                      />
                    </div>
                  )}
                  <div className="full">
                    <label htmlFor="cf-notes">Hinweise (optional)</label>
                    <textarea
                      id="cf-notes"
                      rows={3}
                      maxLength={500}
                      value={consultationNotes}
                      onChange={(e) => setConsultationNotes(e.target.value)}
                      placeholder="Standort, Wünsche, beste Erreichbarkeit…"
                    />
                  </div>
                </div>
              </div>
            )}

            {selectedOption.bookingMode === "mobile" && (
              <div className="booking-step">
                <div className="step-head">
                  <h2>
                    Deine <em>Massageadresse</em>
                  </h2>
                  <span className="booking-step-number">02</span>
                </div>
                <p className="step-summary">
                  {requiresServiceAreaCheck
                    ? "Vor-Ort-Termine sind im gesamten Hamburger Stadtgebiet buchbar. Die Massageadresse wird automatisch geprüft, sobald du das Feld verlässt."
                    : "Wir kommen zu dir. Buchbar im gesamten Hamburger Stadtgebiet."}
                </p>
                {requiresServiceAreaCheck && (
                  <p className="field-note" style={{ marginTop: "0.75rem" }}>
                    Für die Planung blocken wir je nach Personenanzahl den passenden Zeitraum.
                  </p>
                )}
                {/* ServiceAreaMap temporarily hidden until design is final */}
                <div className="address-grid">
                  {needsBookingPhone && (
                    <div className="full">
                      <label htmlFor="ad-phone">Telefonnummer (optional)</label>
                      <input
                        id="ad-phone"
                        type="tel"
                        autoComplete="tel"
                        placeholder="+49 ..."
                        value={bookingPhone}
                        onChange={(e) => setBookingPhone(e.target.value)}
                      />
                    </div>
                  )}
                  {showPersonCount && !isWakeupPlusOption(selectedOption) && (
                    <div className="full">
                      <label htmlFor="person-count-mobile">{personCountLabel} *</label>
                      {renderPersonCountInput("person-count-mobile")}
                    </div>
                  )}
                  <div className="full">
                    <label htmlFor="ad-company">Name / Firma *</label>
                    <input
                      id="ad-company"
                      {...invalidFieldProps("ad-company")}
                      value={address.companyName}
                      onChange={updateAddress("companyName")}
                      autoComplete="name"
                    />
                    {requiresOnsiteBusinessIdentity && (
                      <p className="field-note">Name oder Firma am Einsatzort.</p>
                    )}
                  </div>
                  {selectedOption.usesLegacyAddressFields ? (
                    <>
                      <div className="full">
                        <label htmlFor="ad-street">Straße &amp; Hausnummer *</label>
                        <input
                          id="ad-street"
                          {...invalidFieldProps("ad-street")}
                          value={address.street}
                          onChange={updateAddress("street")}
                          autoComplete="street-address"
                        />
                      </div>
                      <div>
                        <label htmlFor="ad-plz">PLZ *</label>
                        <input
                          id="ad-plz"
                          {...invalidFieldProps("ad-plz")}
                          inputMode="numeric"
                          maxLength={5}
                          value={address.postalCode}
                          onChange={updateAddress("postalCode")}
                          autoComplete="postal-code"
                        />
                        {address.postalCode.length === 5 && (
                          <div className={`plz-status ${plzIsValid ? "is-ok" : "is-bad"}`}>
                            {plzIsValid
                              ? "PLZ liegt im Hamburger Stadtgebiet."
                              : "PLZ liegt außerhalb des Hamburger Stadtgebiets."}
                          </div>
                        )}
                      </div>
                      <div>
                        <label htmlFor="ad-city">Ort *</label>
                        <input
                          id="ad-city"
                          {...invalidFieldProps("ad-city")}
                          value={address.city}
                          onChange={updateAddress("city")}
                          autoComplete="address-level2"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="full">
                      <label htmlFor="ad-full-address">
                        Massageadresse *
                      </label>
                      <input
                        id="ad-full-address"
                        {...invalidFieldProps("ad-full-address")}
                        value={address.fullAddress}
                        onChange={updateAddress("fullAddress")}
                        onBlur={requiresServiceAreaCheck ? handleFullAddressBlur : undefined}
                        placeholder="Straße Hausnummer, PLZ Ort"
                        autoComplete="street-address"
                      />
                      {addressSuggestions.length > 0 && (
                        <div className="address-suggestions">
                          {addressSuggestions.map((suggestion, idx) => {
                            const label =
                              typeof suggestion === "string"
                                ? suggestion
                                : suggestion?.label ||
                                  suggestion?.address ||
                                  suggestion?.fullAddress ||
                                  "";
                            if (!label) return null;
                            return (
                              <button
                                type="button"
                                key={`${label}-${idx}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => applyAddressSuggestion(suggestion)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {requiresServiceAreaCheck && (
                        <div className="service-area-check">
                          {serviceAreaCheck.message ? (
                            <div
                              className={`service-area-status is-${serviceAreaCheck.status}`}
                            >
                              {serviceAreaCheck.message}
                            </div>
                          ) : null}
                          {(serviceAreaCheck.status === "outside" ||
                            serviceAreaCheck.status === "unknown") && (
                            <Link
                              to={`/kontakt?reason=outside-area&offer=${isWakeupPlusOption(selectedOption) ? "wakeup-plus" : selectedOption.id}&address=${encodeURIComponent(selectedFullAddress)}`}
                              className="btn-ghost"
                            >
                              Sonderanfrage stellen
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {renderBusinessInvoiceAddress()}
                  <div className="full">
                    <label htmlFor="ad-notes">Hinweise (optional)</label>
                    <textarea
                      id="ad-notes"
                      rows={3}
                      maxLength={300}
                      value={address.notes}
                      onChange={updateAddress("notes")}
                      autoComplete="off"
                      placeholder="Stockwerk, Klingelschild, Hinweise zum Ablauf…"
                    />
                  </div>
                </div>
              </div>
            )}

            {isStudioLikeBooking(selectedOption.bookingMode) && (
              <div className="booking-step">
                <div className="step-head">
                  <h2>
                    <em>Ort</em>
                  </h2>
                  <span className="booking-step-number">02</span>
                </div>
                <div className="address-grid">
                  <div className="full">
                    <p className="step-summary" style={{ marginTop: 0 }}>
                      <strong>{selectedStudioLocation}</strong>
                    </p>
                  </div>
                  {renderBusinessInvoiceAddress()}
                  {showPersonCount && (
                    <div className="full">
                      <label htmlFor="person-count-studio">{personCountLabel} *</label>
                      {renderPersonCountInput("person-count-studio")}
                    </div>
                  )}
                  {needsBookingPhone && (
                    <div className="full">
                      <label htmlFor="booking-phone-studio">Telefonnummer (optional)</label>
                      <input
                        id="booking-phone-studio"
                        type="tel"
                        autoComplete="tel"
                        placeholder="+49 ..."
                        value={bookingPhone}
                        onChange={(e) => setBookingPhone(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {showPaymentStep && (
              <div className="booking-step">
                <div className="step-head">
                  <h2>
                    <em>Zahlung</em>
                  </h2>
                  <span className="booking-step-number">
                    {hasDetailsStep ? "03" : "02"}
                  </span>
                </div>
                <p className="step-summary">
                  {canUseReferralBalance && !showPaymentMethodChoice
                    ? "Dein Guthaben deckt den gesamten Betrag."
                    : canUseReferralBalance && showPaymentMethodChoice
                      ? onsitePaymentDisabled
                        ? "Wähle, ob du Guthaben einlösen möchtest. Den Restbetrag bezahlst du online."
                        : "Wähle die Zahlungsart und ob du Guthaben einlösen möchtest."
                      : onlinePaymentOnly
                    ? "Diese Buchung ist ausschließlich online zahlbar."
                    : onsitePaymentDisabled
                      ? "Diese Buchung bezahlst du online."
                    : isBusinessEventOption(selectedOption)
                      ? "Wähle, ob du online oder vor Ort bei der WM-Aktion bezahlen möchtest."
                      : "Wähle, ob du online oder vor Ort beim Termin bezahlen möchtest."}
                </p>
                {showPaymentMethodChoice && (
                <div className="payment-options">
                  <label
                    className={`payment-option ${onlinePaymentOnly || effectivePaymentMethod === "stripe" ? "is-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="stripe"
                      checked={onlinePaymentOnly || effectivePaymentMethod === "stripe"}
                    onChange={(event) => {
                      setPaymentMethod("stripe");
                      scrollToNextBookingStep(event.currentTarget);
                    }}
                    />
                    <div className="payment-option-info">
                      <strong className="payment-option-title">
                        Online bezahlen
                        {showOnlineSavingsBadge && (
                          <span className="payment-option-badge">
                            Zahle online und spare Geld
                          </span>
                        )}
                      </strong>
                      <span>
                        Nach dem Bestätigen leiten wir dich zu Stripe weiter.
                      </span>
                    </div>
                  </label>
                  {onsitePaymentDisabled && !onlinePaymentOnly && (
                    <div className="payment-onsite-info">
                      <strong>Vor Ort bezahlen?</strong>
                      <p>
                        Wenn du vor Ort bar zahlen möchtest, <b>zahlst du mehr</b>. Um das zu umgehen, kannst du dein Kontoguthaben{" "}
                        <Link to="/aufladen">hier aufladen</Link>.
                      </p>
                      <p>
                        Alternativ kannst du gerne zu uns zum Cremon 11, 20457 Hamburg kommen und dein Kontoguthaben mit Bargeld aufladen.
                      </p>
                    </div>
                  )}
                  {!onlinePaymentOnly && !onsitePaymentDisabled && (
                    <label
                      className={`payment-option ${effectivePaymentMethod === "on-site" ? "is-selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="payment"
                        value="on-site"
                        checked={effectivePaymentMethod === "on-site"}
                        onChange={(event) => {
                          setPaymentMethod("on-site");
                          scrollToNextBookingStep(event.currentTarget);
                        }}
                      />
                      <div className="payment-option-info">
                        <strong>Vor Ort bezahlen</strong>
                        <span>
                          {isBusinessEventOption(selectedOption)
                            ? "Bar oder Karte direkt vor Ort bei der WM-Aktion."
                            : isStudioLikeBooking(selectedOption.bookingMode)
                            ? "Bar oder Karte direkt im Studio beim Termin."
                            : "Bar oder Karte beim Termin vor Ort bei dir."}
                        </span>
                      </div>
                    </label>
                  )}
                </div>
                )}
                {canUseReferralBalance && (
                  <label className="referral-balance-option">
                    <input
                      type="checkbox"
                      checked={applyReferralBalance}
                      onChange={(e) => setApplyReferralBalance(e.target.checked)}
                    />
                    <span>
                      Guthaben verwenden ({formatPrice(referralBalanceCents)})
                    </span>
                  </label>
                )}
              </div>
            )}

            <div className="booking-step">
              <div className="step-head">
                <h2>
                  <em>Gutschein</em>
                </h2>
              </div>
              {user && matchingMassageVouchers.length > 0 ? (
                <>
                  <p className="field-note">
                    Du kannst einen passenden Gutschein für diese Massage verwenden. Die Buchung ist dann kostenfrei.
                  </p>
                  {matchingMassageVouchers.map((voucher) => (
                    <label key={voucher.id} className="booking-voucher-option">
                      <input
                        type="checkbox"
                        checked={selectedMassageVoucher?.id === voucher.id}
                        onChange={(event) =>
                          setSelectedMassageVoucherId(event.target.checked ? voucher.id : "")
                        }
                      />
                      <span>
                        Gutschein verwenden
                        <small>
                          {voucher.label} · {voucher.code}
                        </small>
                      </span>
                    </label>
                  ))}
                </>
              ) : !user ? (
                <p className="field-note">
                  Melde dich an, um einen eingelösten Gutschein zu verwenden.
                </p>
              ) : null}
              {user && redeemedVouchers.length === 0 && (
                <p className="field-note">
                  Du hast noch keinen eingelösten Gutschein.{" "}
                  <Link to="/shop">Im Shop kaufen</Link>
                  {" "}oder im <Link to="/konto/gutscheine">Konto einlösen</Link>.
                </p>
              )}
              {user && redeemedVouchers.length > 0 && matchingMassageVouchers.length === 0 && (
                <p className="field-note">
                  Keiner deiner eingelösten Gutscheine passt zu dieser Auswahl.
                </p>
              )}
            </div>

            <div className="booking-step">
              <div className="step-head">
                <h2>
                  Deine <em>Buchung</em>
                </h2>
                <span className="booking-step-number">{summaryStepNumber}</span>
              </div>
              <div className="booking-summary-block" style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}>
                <div className="summary-row">
                  <span>Leistung</span>
                  <strong>{selectedOption.title}</strong>
                </div>
                {selectedOption.voucherType && (
                  <div className="summary-row">
                    <span>Kostenfreier Erstbesuch</span>
                    <strong>
                      {voucherActive
                        ? (
                          <span className="booking-free-visit-label">
                            Kostenfreier Erstbesuch
                          </span>
                        )
                        : "Bereits eingelöst"}
                    </strong>
                  </div>
                )}
                {!isBusinessEventOption(selectedOption) && (
                  <div className="summary-row">
                    <span>Dauer</span>
                    <strong>
                      {isWakeupBookingOption(selectedOption)
                        ? isWakeupPlusOption(selectedOption)
                          ? "bis zu 30 Min."
                          : `${selectedPersonCount} × ${WAKEUP_MINUTES_PER_PERSON} Min.`
                        : durationChoiceMissing
                          ? "Bitte wählen"
                          : isIntensePlusBusinessOption(selectedOption)
                            ? `${selectedDurationMinutes / 60} ${selectedDurationMinutes === 60 ? "Stunde" : "Stunden"}`
                            : `${selectedDurationMinutes} Min.`}
                    </strong>
                  </div>
                )}
                {showPersonCount && (
                  <div className="summary-row">
                    <span>{personCountLabel}</span>
                    <strong>{selectedPersonCount}</strong>
                  </div>
                )}
                {isWakeupPlusOption(selectedOption) && monthlyActionVariant && (
                  <div className="summary-row">
                    <span>{monthlyAction.label}</span>
                    <strong>
                      {MONTHLY_ACTION_VARIANTS.find((item) => item.id === monthlyActionVariant)?.label}
                    </strong>
                  </div>
                )}
                {isBusinessEventOption(selectedOption) ? (
                  <div className="summary-row">
                    <span>Datum &amp; Uhrzeit</span>
                    <strong>{HOFFMANN_BUSINESS_EVENT_SCHEDULE_LABEL}</strong>
                  </div>
                ) : (
                  <>
                    <div className="summary-row">
                      <span>Datum</span>
                      <strong>
                        {appointmentIsArranged
                          ? "Nach Vereinbarung"
                          : selectedDate
                            ? formatLong(selectedDate)
                            : "—"}
                      </strong>
                    </div>
                    <div className="summary-row">
                      <span>Uhrzeit</span>
                      <strong>
                        {appointmentIsArranged
                          ? "Nach Vereinbarung"
                          : selectedSlot
                            ? formatTime(selectedSlot)
                            : "—"}
                      </strong>
                    </div>
                  </>
                )}
                {selectedOption.bookingMode !== "mobile" && (
                  <div className="summary-row">
                    <span>Ort</span>
                    <strong>
                      {isBusinessEventOption(selectedOption)
                        ? selectedOption.eventLocation || HOFFMANN_BUSINESS_EVENT_LOCATION
                        : isFreeConsultation(selectedOption)
                        ? "Cremon 11, 20457 Hamburg"
                        : selectedOption.bookingMode === "consultation"
                          ? "Nach Vereinbarung"
                          : isStudioLikeBooking(selectedOption.bookingMode)
                            ? selectedStudioLocation
                            : "—"}
                    </strong>
                  </div>
                )}
                {selectedOption.bookingMode === "mobile" && (
                  <div className="summary-row">
                    <span>Einsatzort</span>
                    <strong>
                      {selectedFullAddress
                        ? selectedFullAddress
                        : "Bitte Massageadresse prüfen"}
                    </strong>
                  </div>
                )}
                {selectedOption.bookingMode === "mobile" && (
                  <div className="summary-row">
                    <span>Gebiet</span>
                    <strong>
                      {addressIsEligible
                        ? "Massageadresse geprüft"
                        : serviceAreaCheck.status === "checking"
                          ? "Prüfung läuft"
                          : serviceAreaCheck.status === "idle"
                            ? "Noch nicht geprüft"
                            : "Massageadresse nicht im Gebiet"}
                    </strong>
                  </div>
                )}
                {selectedOption.requiresVoucherQuestion && !selectedMassageVoucher && (
                  <div className="summary-row">
                    <span>Gutschein</span>
                    <strong>
                      {hasVoucher === null ? "Bitte auswählen" : hasVoucher ? "Ja" : "Nein"}
                    </strong>
                  </div>
                )}
                {selectedOption.badge && (
                  <div className="summary-row">
                    <span>Hinweis</span>
                    <strong>{selectedOption.badge}</strong>
                  </div>
                )}
                <div className="summary-row">
                  <span>Zahlung</span>
                  <strong>
                    {isFreeConsultation(selectedOption)
                      ? (
                        <span className="booking-free-visit-label">
                          Kostenfreier Erstbesuch
                        </span>
                      )
                      : selectedMassageVoucher
                        ? "Massage-Gutschein"
                      : voucherActive
                        ? (
                          <span className="booking-free-visit-label">
                            Kostenfreier Erstbesuch
                          </span>
                        )
                      : isFreeDueToReferralBalance
                        ? (
                          <span className="booking-free-visit-label">
                            Mit Guthaben
                          </span>
                        )
                      : selectedOption.paymentLocked
                        ? "Vor Ort"
                      : onlinePaymentOnly
                        ? "Online"
                      : effectivePaymentMethod === "stripe"
                        ? "Online"
                        : "Vor Ort"}
                  </strong>
                </div>
                {selectedOption.paidPackage && selectedOption.priceNetCents && (
                  <>
                    <div className="summary-row">
                      <span>Nettobetrag</span>
                      <strong>{formatPrice(selectedOption.priceNetCents)}</strong>
                    </div>
                    <div className="summary-row">
                      <span>MwSt.</span>
                      <strong>{formatPrice(packageVatCents)}</strong>
                    </div>
                  </>
                )}
                {currentPricePreview && (
                  <>
                    {currentPricePreview.serviceNetCents !== undefined &&
                      !isBusinessEventOption(selectedOption) && (
                      <div className="summary-row">
                        <span>
                          {isFixedGrossIndividualOption(selectedOption)
                            ? "Leistung"
                            : "Leistung netto"}
                        </span>
                        <strong>{formatPrice(currentPricePreview.serviceNetCents)}</strong>
                      </div>
                    )}
                    {currentPricePreview.travelNetCents !== undefined &&
                      !isBusinessEventOption(selectedOption) && (
                      <div className="summary-row">
                        <span>Anfahrt</span>
                        <strong>
                          {currentPricePreview.travelOriginalNetCents &&
                          currentPricePreview.travelOriginalNetCents !==
                            currentPricePreview.travelNetCents ? (
                            <>
                              {formatPrice(currentPricePreview.travelNetCents)}{" "}
                              <span
                                style={{
                                  textDecoration: "line-through",
                                  color: "var(--grey-mid)",
                                  fontWeight: 500,
                                }}
                              >
                                {formatPrice(currentPricePreview.travelOriginalNetCents)}
                              </span>
                            </>
                          ) : (
                            formatPrice(currentPricePreview.travelNetCents)
                          )}
                        </strong>
                      </div>
                    )}
                    {includedPrivateVatCents !== null && (
                      <div className="summary-row">
                        <span>Enthaltene MwSt. (19 %)</span>
                        <strong>{formatPrice(includedPrivateVatCents)}</strong>
                      </div>
                    )}
                    {!isFixedGrossIndividualOption(selectedOption) &&
                      !isBusinessEventOption(selectedOption) && (
                      <>
                        <div className="summary-row">
                          <span>Netto gesamt</span>
                          <strong>{formatPrice(currentPricePreview.netCents)}</strong>
                        </div>
                        <div className="summary-row">
                          <span>MwSt. 19 %</span>
                          <strong>{formatPrice(currentPricePreview.vatCents)}</strong>
                        </div>
                      </>
                    )}
                  </>
                )}
                <div className="total">
                  <span>{currentPricePreview ? "Zu zahlen" : "Gesamt"}</span>
                  <strong>
                    {currentPricePreview ? (
                      <>
                        {selectedMassageVoucher && (
                          <span className="booking-price-struck">
                            {formatPrice(currentPricePreview.grossCents)}
                          </span>
                        )}
                        {formatPrice(
                          selectedMassageVoucher
                            ? 0
                            : referralCreditPreviewCents > 0
                              ? payableAfterReferralCents
                              : currentPricePreview.grossCents
                        )}
                        {selectedMassageVoucher && (
                          <small style={{ display: "block", fontWeight: 500 }}>
                            mit Massage-Gutschein
                          </small>
                        )}
                        {referralCreditPreviewCents > 0 && (
                          <small style={{ display: "block", fontWeight: 500 }}>
                            inkl. {formatPrice(referralCreditPreviewCents)} Guthaben
                          </small>
                        )}
                        <small
                          className={`booking-price-check ${loadingPricePreview ? "is-loading" : ""}`}
                          aria-live="polite"
                        >
                          Preis wird geprüft...
                        </small>
                      </>
                    ) : isFreeConsultation(selectedOption) ? (
                      <>
                        <span
                          className="option-price-struck"
                          style={{
                            marginRight: "0.5rem",
                            fontSize: "0.9em",
                          }}
                        >
                          {selectedOption.price}{" "}
                          <small>{selectedOption.priceNote}</small>
                        </span>
                        <span className="booking-free-visit-label">
                          Kostenfreier Erstbesuch
                        </span>
                      </>
                    ) : voucherActive ? (
                      <>
                        <span
                          className="option-price-struck"
                          style={{
                            marginRight: "0.5rem",
                            fontSize: "0.9em",
                          }}
                        >
                          {formatPrice(paidBasePriceCents)}
                        </span>
                        <span className="booking-free-visit-label">
                          Kostenfreier Erstbesuch
                        </span>
                      </>
                    ) : durationChoiceMissing ? (
                      selectedOption.price
                    ) : (
                      <>
                        {selectedMassageVoucher && Number.isFinite(effectivePriceCents) && (
                          <span className="booking-price-struck">
                            {formatPrice(effectivePriceCents)}
                          </span>
                        )}
                        {formatPrice(
                          selectedMassageVoucher
                            ? 0
                            : referralCreditPreviewCents > 0
                              ? payableAfterReferralCents
                              : effectivePriceCents
                        )}
                        {selectedMassageVoucher && (
                          <small style={{ display: "block", fontWeight: 500 }}>
                            mit Massage-Gutschein
                          </small>
                        )}
                        {referralCreditPreviewCents > 0 && (
                          <small style={{ display: "block", fontWeight: 500 }}>
                            inkl. {formatPrice(referralCreditPreviewCents)} Guthaben
                          </small>
                        )}
                      </>
                    )}
                  </strong>
                </div>
              </div>

              {error && (
                <div className="auth-alert is-error" style={{ marginTop: "1rem" }}>
                  {error}
                  {errorAction && (
                    <div style={{ marginTop: "0.75rem" }}>
                      <Link to={errorAction.href} className="btn-ghost">
                        {errorAction.label}
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {validationAttempted && missingFields.length > 0 && (
                <div className="booking-missing-fields" role="alert">
                  <strong>Bitte ergänze noch:</strong>
                  <ul>
                    {missingFields.map(({ id, label }, index) => (
                      <li key={`${id}-${index}`}>{label}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className={`booking-consent-panel ${validationAttempted && missingFieldIds.has("booking-terms") ? "booking-field-invalid" : ""}`} aria-label="Rechtliche Bestätigungen">
                <div className="booking-terms-notice">
                  <strong>Hinweis zur Terminbuchung:</strong> Mit der Buchung wird der
                  ausgewählte Massagetermin verbindlich für Sie reserviert. Bei einer
                  Stornierung gelten unsere Stornierungs- und Guthabenregelungen gemäß{" "}
                  <Link to="/agbs#storno" target="_blank" rel="noopener noreferrer">
                    Ziffer 6 der AGB
                  </Link>
                  . Bei Nichterscheinen besteht grundsätzlich kein Anspruch auf
                  Umbuchung oder Massageguthaben.
                </div>
                <label className="booking-checkbox">
                  <input
                    type="checkbox"
                    checked={bookingConsents.legalTerms}
                    onChange={(e) =>
                      setBookingConsents((current) => ({
                        ...current,
                        legalTerms: e.target.checked,
                      }))
                    }
                  />
                  <span>
                    <Link to="/agbs#storno" target="_blank" rel="noopener noreferrer">
                      <strong>Ich akzeptiere die AGB</strong>
                    </Link>{" "}
                    einschließlich der Regelungen zu Terminbuchung, Stornierung und
                    Nichterscheinen. Die{" "}
                    <Link to="/datenschutz" target="_blank" rel="noopener noreferrer">
                      Datenschutzerklärung
                    </Link>{" "}
                    habe ich zur Kenntnis genommen.
                  </span>
                </label>
              </div>

              {!user && selectedOption.requiresAuth && (
                <div className="booking-auth-panel">
                  <p>
                    Zum Abschließen benötigen wir ein Kundenkonto. Deine Auswahl
                    bleibt erhalten, wenn du dich anmeldest oder kostenlos registrierst.
                  </p>
                  <div className="booking-auth-actions">
                    <Link
                      to={buildBuchenAuthHref("/einloggen", selectedOption.id)}
                      className="btn-primary"
                      onClick={persistDraft}
                    >
                      Anmelden
                    </Link>
                    <Link
                      to={buildBuchenAuthHref("/registrieren", selectedOption.id)}
                      className="btn-outline"
                      onClick={persistDraft}
                    >
                      Kostenlos registrieren
                    </Link>
                  </div>
                </div>
              )}

              <div className="booking-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => {
                    setSelectedOption(null);
                    scrollToSlotsAfterDateSelectionRef.current = false;
                    setValidationAttempted(false);
                    const clearedParams = new URLSearchParams(location.search);
                    clearedParams.delete("option");
                    const clearedQuery = clearedParams.toString();
                    navigate(clearedQuery ? `/buchen?${clearedQuery}` : "/buchen", {
                      replace: true,
                    });
                  }}
                >
                  Andere Option
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={
                    submitting ||
                    businessEventBookingClosed ||
                    loadingSlots
                  }
                  onClick={submitAuthedBooking}
                >
                  {submitting
                    ? "Buchung läuft…"
                    : !user && selectedOption.requiresAuth
                      ? "Buchung abschließen"
                    : isFreeConsultation(selectedOption)
                      ? "Kostenfreien Termin anfragen"
                    : voucherActive || isFreeDueToReferralBalance || selectedMassageVoucher
                      ? "Jetzt buchen"
                      : onlinePaymentOnly && payableAfterReferralCents > 0
                        ? "Zur Zahlung"
                      : effectivePaymentMethod === "stripe" && isPaidBooking && payableAfterReferralCents > 0
                        ? "Zur Zahlung"
                        : "Jetzt verbindlich buchen"}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
      <OfferDetailsModal
        offerId={openOfferDetails}
        onClose={() => setOpenOfferDetails(null)}
      />
    </Layout>
  );
};

export default BookingPage;


